import * as m from '@staiter/i18n';
import type { AdminInvite, AdminUser, BulkInviteResult, Role } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Bot,
  Check,
  Copy,
  KeyRound,
  Link2,
  Lock,
  LogIn,
  Mail,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ShieldCheck,
  UserCog,
  UserX,
  X as XIcon,
} from 'lucide-react';
import { AlertTriangle, Info, MailWarning } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { InviteDeliveryStatus } from '~/components/domain/InviteDeliveryStatus';
import { InviteOptionsStatus } from '~/components/domain/InviteOptionsStatus';
import { UserEditDrawer } from '~/components/domain/UserEditDrawer';
import { type ToastItem, useToast } from '~/components/feedback';
import { Button, ConfirmDialog, Dialog, Input, Select, Tooltip } from '~/components/primitives';
import {
  type ContextMenuNode,
  type OpenContextMenuArgs,
  openContextMenuFromButton,
} from '~/components/primitives/ContextMenu';
import { StatusBadge } from '~/components/status';
import { bulkDeliverySummary, copyAllVariant } from '~/lib/admin/bulk-delivery';
import {
  alreadyMemberIn,
  bulkInviteRoles,
  fitInviteRole,
  inviteOptionsView,
  rolesFor,
} from '~/lib/admin/invite-options';
import { inviteChannelDownSince, replacedInviteIds } from '~/lib/admin/invite-replaced';
import {
  type RegistryRowAction,
  registryCapabilities,
  registryRoles,
  registryRowActions,
} from '~/lib/admin/registry-actions';
import {
  type RegistryRoleContext,
  registryFocusEntity,
  registryRole,
  registryRoleFilterScope,
} from '~/lib/admin/registry-role';
import { isRestoreLocked, isRowDisabled, restoreScope } from '~/lib/admin/user-disabled';
import {
  type UsersFilter,
  bulkInviteUsers,
  fetchInviteOptions,
  fetchInvites,
  fetchUsers,
  forceUserPasswordReset,
  impersonateUser,
  inviteUserMemberships,
  inviteUserTarget,
  resendInvite,
  revokeInvite,
  rotateServiceSecret,
  setUserActive,
  setUserFullName,
} from '~/lib/api/admin';
import { tokenStorage } from '~/lib/api/client';
import { updateEntityMembership, useEntities, useHoldings } from '~/lib/api/tenancy';
import { bulkFailedReasonText, inviteErrorCode, inviteErrorText } from '~/lib/errors/invite-codes';
import { useLocale } from '~/lib/hooks/useLocale';
import { ENTITY_MEMBERSHIP_ROLES, getRoleLabel } from '~/lib/rbac';
import { useEffectiveRole, useIsSuperAdmin } from '~/lib/rbac/use-tenancy-rbac';
import { notifTime, notifTimeTitle } from '~/lib/tasks/format/relativeTime';
import { useActiveEntityStore } from '~/store/active-entity';
import { useAuthStore } from '~/store/auth';
import { useImpersonationStore } from '~/store/impersonation';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

/**
 * Адрес переносится перед «@», а не по дефису: дефисы — неразрывные (U+2011), перед «@» — `<wbr>`.
 * Не влезает и так — переносит `overflow-wrap: anywhere` ячейки. Полный адрес — в подсказке.
 */
function EmailWrap({ text, soft = false }: { text: string; soft?: boolean }): JSX.Element {
  const at = text.indexOf('@');
  const nb = (part: string): string => part.replace(/-/g, '‑');
  if (at < 0) return <>{nb(text)}</>;
  /*
   * F109, круг 4 (UI, MAJOR): в таблице приглашений длинный адрес до «@» рвался посреди слова
   * («address-f / or-columns»). `soft` — точки переноса перед «-» и «.» в части до «@»; реестр (F106)
   * остаётся с переносом только перед «@».
   */
  const local = text.slice(0, at);
  const parts = soft ? local.split(/(?=[-.])/) : [local];
  return (
    <>
      {parts.map((p, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: части одного адреса, порядок не меняется
        <span key={i}>
          {i > 0 && <wbr />}
          {nb(p)}
        </span>
      ))}
      <wbr />
      {nb(text.slice(at))}
    </>
  );
}

export const Route = createFileRoute('/_auth/admin/users')({
  staticData: { minMainWidth: 1284 },
  component: UsersAdmin,
});

/**
 * Отображаемое имя (как в Teams) — inline-редактируемая ячейка. Клик → инпут;
 * Enter/blur сохраняет, Esc отменяет. Пусто → приглушённый плейсхолдер.
 */
function EditableName({
  value,
  fallback,
  onSave,
  readOnly = false,
}: {
  value: string;
  fallback: string;
  onSave: (v: string) => void;
  /** С-I8 Р.5: руководителю компании имя не правится (`PUT /admin/users/:id` — от `admin`). */
  readOnly?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  if (readOnly) {
    return (
      <span className={styles.nameButton} style={{ cursor: 'default' }}>
        <span className={value ? styles.nameText : `${styles.nameText} ${styles.nameLogin}`}>
          {value || fallback.split('@')[0] || '—'}
        </span>
        {!value && <span className={styles.nameHint}>{m.admin_users_name_missing()}</span>}
      </span>
    );
  }
  if (editing) {
    return (
      <Input
        label=""
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft.trim() !== value) onSave(draft.trim());
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
        placeholder={fallback}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      title={m.admin_users_set_display_name()}
      className={styles.nameButton}
      style={{
        color: value ? 'var(--text-primary)' : 'var(--text-muted)',
        fontWeight: value ? 500 : 400,
      }}
    >
      {/*
        Раунд 3 гейта T2A-F5: прочерк вместо имени не опознаёт человека, а
        соседний адрес обрезается ровно на домене. Показываем локальную часть
        адреса — она и есть то, чем такого пользователя называют в переписке;
        грейд и вес остаются приглушёнными, чтобы «имя не заполнено» читалось.
      */}
      {/* F27 круг 3 (UI, MAJOR): многоточие резало пометку до «имя н…» — теперь оно
          только у самого имени, пометка стоит целиком. */}
      {/* F106 (круг 1, UX): имя переносится до двух строк; логин вместо имени — одной строкой
          (переносить его посреди слова нечестно), пометка уходит на следующую строку. */}
      <span className={value ? styles.nameText : `${styles.nameText} ${styles.nameLogin}`}>
        {value || fallback.split('@')[0] || '—'}
      </span>
      {!value && <span className={styles.nameHint}>{m.admin_users_name_missing()}</span>}
    </button>
  );
}

function UsersAdmin(): JSX.Element {
  const locale = useLocale();
  const qc = useQueryClient();
  // SR-F7 (F21): чип «Компании» показывает компанию текущего контекста, а не первую запись.
  const activeEntityId = useActiveEntityStore((s) => s.activeEntityId);
  const activeScope = useActiveEntityStore((s) => s.activeScope);
  /*
   * С-I8, гейт круг 2 (UI-2, UX-3, ACC-F36): в охвате холдинга реестр — люди компаний холдинга
   * (`holding_id`, С-I8 Р.2), и у суперадмина тоже: заголовок «Пользователи холдинга» под баннером
   * холдинга не должен расходиться со списком всех людей платформы.
   */
  const scopeFilter: UsersFilter =
    typeof activeScope === 'object' ? { holding_id: activeScope.holdingId } : {};
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', scopeFilter],
    queryFn: () => fetchUsers(scopeFilter),
  });
  /**
   * F19 (SR-F15, пак tails-closeout): «найти человека» решалось глазами по списку.
   * Отбор — на сервере (B46: search / role / entity_id / is_active). Полный список
   * остаётся источником вариантов «Компании»: при отборе по компании выдача
   * сужается, а варианты пропадать не должны. Запросы под ключом `['admin','users']`
   * — те же инвалидации мутаций обновляют и отобранную выдачу.
   */
  const [searchText, setSearchText] = useState('');
  const [searchQ, setSearchQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(searchText.trim()), 250);
    return () => clearTimeout(t);
  }, [searchText]);
  const [roleF, setRoleF] = useState('');
  const [entityF, setEntityF] = useState('');
  const [activeF, setActiveF] = useState<'' | 'true' | 'false'>('');
  const entitiesForTitle = useEntities();
  // С-I7 Р.4 (ACC-F9): контекст колонки «Роль» — охват, отбор «Компания», компании холдинга.
  const holdingId = typeof activeScope === 'object' ? activeScope.holdingId : null;
  const holdingEntityIds = useMemo(
    () =>
      holdingId && entitiesForTitle.data
        ? new Set(
            entitiesForTitle.data.items.filter((e) => e.holding_id === holdingId).map((e) => e.id),
          )
        : null,
    [holdingId, entitiesForTitle.data],
  );
  const registryCtx: RegistryRoleContext = {
    activeScope,
    activeEntityId,
    entityFilter: entityF,
    holdingEntityIds,
  };
  /*
   * С-I7 Р.4 (ACC-F9), С-I8 Р.2 (ACC-F28): отбор «Роль» и колонка смотрят на одно множество
   * компаний — компания, компании холдинга или область смотрящего (`registryRoleFilterScope`).
   */
  const userPicks: UsersFilter = {
    ...(searchQ ? { search: searchQ } : {}),
    ...(roleF ? { role: roleF } : {}),
    ...(entityF ? { entity_id: entityF } : {}),
    ...(roleF ? registryRoleFilterScope(registryCtx) : {}),
    ...(activeF ? { is_active: activeF === 'true' } : {}),
  };
  const filterActive = Object.keys(userPicks).length > 0;
  const usersFilter: UsersFilter = { ...scopeFilter, ...userPicks };
  const filteredQuery = useQuery({
    queryKey: ['admin', 'users', 'filtered', usersFilter],
    queryFn: () => fetchUsers(usersFilter),
    enabled: filterActive,
    placeholderData: (prev) => prev,
  });
  const entityOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const u of (data?.items ?? []) as AdminUser[]) {
      for (const mem of u.entity_memberships ?? []) {
        if (!seen.has(mem.entity_id)) seen.set(mem.entity_id, mem.entity_name ?? mem.entity_id);
      }
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [data]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmImpersonate, setConfirmImpersonate] = useState<AdminUser | null>(null);
  /** B9 (service-role) — секрет живёт в состоянии ровно до закрытия диалога. */
  const [rotatedSecret, setRotatedSecret] = useState<{ secret: string; revoked: number } | null>(
    null,
  );
  /**
   * Заход 4 гейта, блокер UX: ротация — необратимое действие, кладущее живую
   * интеграцию до ручной правки её конфигурации. Один клик в ряду с безобидными
   * «Изменить» / «Доступы» для такого не годится: сперва сказать, что произойдёт.
   */
  const [confirmRotate, setConfirmRotate] = useState<AdminUser | null>(null);
  const toast = useToast();
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const startImpersonate = useImpersonationStore((s) => s.start);
  // С-I2 Р.10, круг 1 (UX): отключал сам смотрящий — «отключили вы».
  const meId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? null);
  const navigate = useNavigate();
  const isSuper = useIsSuperAdmin();
  const effRole = useEffectiveRole();
  // С-I8 Р.5 (В-59, ACC-F18): руководителю компании — только действия с открытыми ему ручками.
  const caps = registryCapabilities({ isSuper, role: effRole });
  const activeEntityName =
    (entitiesForTitle.data?.items ?? []).find((e) => e.id === activeEntityId)?.name ?? null;
  const registryFocus = registryFocusEntity(registryCtx);
  /** F106 (круг 1, UX MAJOR): возврат аккаунта — с подтверждением объёма. */
  const [confirmRestore, setConfirmRestore] = useState<AdminUser | null>(null);

  // FM-S-multi-6-4: заголовок scoped по RBAC (semantic clarity per UX verdict).
  // С-I8, гейт круги 1–2 (UI-3, UI-2, UX-3, ACC-F36): заголовок — от охвата, и у суперадмина; название
  // компании — только в охвате одной компании.
  const scopedTitle =
    typeof activeScope === 'object' || (!isSuper && effRole === 'holding_admin')
      ? m.route_admin_users_title_holding()
      : isSuper || activeScope === 'all'
        ? m.route_admin_users_title_super()
        : effRole === 'company_admin' || effRole === 'admin'
          ? activeEntityName
            ? // F106 (круг 1, UX): заголовок называет компанию, как вкладка — объект («Пользователи»).
              m.route_admin_users_title_company_named({ company: activeEntityName })
            : m.route_admin_users_title_company()
          : m.route_admin_users_title();

  const items = ((filterActive ? filteredQuery.data?.items : data?.items) ?? []) as AdminUser[];
  const listLoading = filterActive ? filteredQuery.isLoading : isLoading;

  const nameMut = useMutation({
    mutationFn: (input: { id: string; fullName: string }) =>
      setUserFullName(input.id, input.fullName),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  const activeMut = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => setUserActive(input.id, input.active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  /**
   * F106 (С-I, NC-53; К5-7): «Вернуть доступ» — по виду отключения: отключённое членство
   * включается членством, выключенный аккаунт — аккаунтом.
   */
  const restoreMut = useMutation({
    mutationFn: (u: AdminUser) =>
      restoreScope(u) === 'membership' && u.disabled?.entity_id
        ? updateEntityMembership(u.id, u.disabled.entity_id, { status: 'active' })
        : setUserActive(u.id, true),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  /**
   * С-I2 Р.6 (В-48): админ компании отключает сотрудника членством в своей компании; весь аккаунт —
   * только администратор платформы. В «Все компании» и в холдинге «своей» компании нет — пункта нет.
   */
  const ownCompanyId = !isSuper && activeScope === 'entity' ? activeEntityId : null;
  const membershipOffMut = useMutation({
    mutationFn: (u: AdminUser) =>
      updateEntityMembership(u.id, ownCompanyId ?? '', { status: 'disabled' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  const resetMut = useMutation({
    mutationFn: (id: string) => forceUserPasswordReset(id),
  });
  const rotateMut = useMutation({
    mutationFn: (id: string) => rotateServiceSecret(id),
    onSuccess: (resp) => {
      setConfirmRotate(null);
      setRotatedSecret({ secret: resp.secret, revoked: resp.revoked_sessions });
    },
  });
  const imperMut = useMutation({
    mutationFn: (id: string) => impersonateUser(id),
    onSuccess: (resp) => {
      tokenStorage.set(resp.access_token, resp.refresh_token);
      startImpersonate(resp.target_email);
      setConfirmImpersonate(null);
      window.location.assign('/');
    },
  });

  /**
   * Меню строки реестра. Состав зависит от субъекта, и это не украшение:
   *
   *  - у машины (`role === 'service'`) нет ни пароля, ни сессии — вместо сброса
   *    по письму ей положена ротация секрета, а «Войти от имени» не показывается
   *    вовсе (заходы 2–3 гейта пака `service-role`: действие, которого у субъекта
   *    не существует, не приглушается — оно отсутствует);
   *  - «Войти от имени» и деактивация стоят последней группой с danger-грейдом:
   *    это ходы, после которых экран перестаёт принадлежать читателю.
   */
  /*
   * F106 (С-I, T2A-F24): в строке одна кнопка — «Изменить» или, у отключённого, «Вернуть доступ»;
   * переход к компаниям и ролям — всегда в меню, и называется не «Доступы» (так зовётся колонка).
   * F106 (круг 2): у отключённого возврат — только кнопкой строки (с подтверждением объёма и запретом,
   * когда отключал невидимый администратор); пункт «Активировать» в меню обходил и то и другое.
   * Круг 2 (UI): опасная группа — всегда за разделителем; (UX) войти от имени отключённого нельзя —
   * пункта у него нет (действие, которого у субъекта нет, отсутствует).
   * С-I8 Р.5: состав — `registryRowActions` (`lib/admin/registry-actions.ts`); руководителю компании —
   * одна кнопка «Доступы», без меню.
   */
  const rowActions = (u: AdminUser) =>
    registryRowActions(
      {
        role: u.role,
        disabled: isRowDisabled(u),
        companyRoles: registryRoles(registryRole(u, registryCtx)),
      },
      { caps, isSuper, ownCompanyId },
    );
  const openAccess = (u: AdminUser): void =>
    void navigate({ to: '/admin/users/$userId/memberships', params: { userId: u.id } });
  const rowMenuNode = (u: AdminUser, a: RegistryRowAction): ContextMenuNode | null => {
    switch (a) {
      case 'edit':
        return {
          kind: 'item',
          id: 'edit',
          label: m.ui_izmenit(),
          icon: UserCog,
          onSelect: () => setEditingUser(u),
        };
      case 'access':
        return {
          kind: 'item',
          id: 'access',
          label: m.route_admin_users_menu_access(),
          icon: ShieldCheck,
          onSelect: () => openAccess(u),
        };
      case 'rotate':
        return {
          kind: 'item',
          id: 'rotate',
          label: m.service_account_rotate_cta(),
          icon: KeyRound,
          onSelect: () => setConfirmRotate(u),
        };
      case 'reset':
        return {
          kind: 'item',
          id: 'reset',
          label: m.route_admin_users_btn_reset(),
          icon: KeyRound,
          onSelect: () => resetMut.mutate(u.id),
        };
      case 'disable-account':
        return {
          kind: 'item',
          id: 'active',
          label: m.route_admin_users_btn_disable_account(),
          icon: UserX,
          danger: true,
          onSelect: () => activeMut.mutate({ id: u.id, active: false }),
        };
      case 'disable-in-company':
        return {
          kind: 'item',
          id: 'membership-off',
          label: m.route_admin_users_btn_disable_in_company(),
          icon: UserX,
          danger: true,
          onSelect: () => membershipOffMut.mutate(u),
        };
      case 'impersonate':
        return {
          kind: 'item',
          id: 'impersonate',
          label: m.route_admin_users_btn_impersonate(),
          // Круг 2 (UI): свой значок — у «Изменить» и «Войти от имени» был один `UserCog`.
          icon: LogIn,
          danger: true,
          onSelect: () => setConfirmImpersonate(u),
        };
      default:
        return null;
    }
  };
  const buildUserRowMenu = (u: AdminUser): OpenContextMenuArgs => {
    const nodes: ContextMenuNode[] = [];
    for (const a of rowActions(u).menu) {
      if (a === 'disable-account' || a === 'disable-in-company') {
        nodes.push({ kind: 'separator', id: 'sep-danger' });
      }
      const node = rowMenuNode(u, a);
      if (node) nodes.push(node);
    }
    return {
      nodes,
      width: 'project',
      ariaLabel: `${m.project_header_more_actions()} · ${u.full_name ?? u.email}`,
    };
  };

  return (
    <div className={shared.page}>
      <PageHeader
        title={scopedTitle}
        description={m.route_admin_users_description()}
        actions={
          <>
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<Plus size={14} />}
              onClick={() => setBulkOpen(true)}
            >
              {m.route_admin_users_btn_bulk_invite()}
            </Button>
            <Button size="sm" leadingIcon={<Plus size={14} />} onClick={() => setInviteOpen(true)}>
              {m.route_admin_users_btn_invite()}
            </Button>
          </>
        }
      />

      {/* F19 (SR-F15): строка отбора над реестром — первой. */}
      <div className={shared.filters} role="search" aria-label={m.route_admin_users_filter_aria()}>
        <Input
          label={m.route_admin_users_filter_search()}
          placeholder={m.route_admin_users_filter_search_placeholder()}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Select
          className={styles.filterSelect}
          label={m.route_admin_users_col_role()}
          value={roleF}
          onChange={(e) => setRoleF(e.target.value)}
          options={[
            { value: '', label: m.route_admin_users_filter_any() },
            ...ENTITY_MEMBERSHIP_ROLES.map((r) => ({ value: r, label: getRoleLabel(r) })),
          ]}
        />
        <Select
          className={styles.filterSelect}
          label={m.route_admin_users_filter_company()}
          value={entityF}
          onChange={(e) => setEntityF(e.target.value)}
          options={[{ value: '', label: m.route_admin_users_filter_any() }, ...entityOptions]}
        />
        <Select
          className={styles.filterSelect}
          label={m.route_admin_users_filter_active()}
          value={activeF}
          onChange={(e) => setActiveF(e.target.value as '' | 'true' | 'false')}
          options={[
            { value: '', label: m.route_admin_users_filter_any() },
            { value: 'true', label: m.route_admin_users_filter_active_yes() },
            { value: 'false', label: m.route_admin_users_filter_active_no() },
          ]}
        />
        {/* Круг 3 (UX, MINOR): общее число видно и без отбора. Круг 3 (UI, MAJOR):
            строка отбора не переносится — оговорка про приглашения ушла к ним. */}
        <span className={styles.filterCount} aria-live="polite">
          {filterActive
            ? m.route_admin_users_filter_count({
                shown: items.length,
                total: (data?.items ?? []).length,
              })
            : m.route_admin_users_filter_total({ total: (data?.items ?? []).length })}
        </span>
        {filterActive && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchText('');
              setSearchQ('');
              setRoleF('');
              setEntityF('');
              setActiveF('');
            }}
          >
            {m.route_admin_users_filter_reset()}
          </Button>
        )}
      </div>

      <Table
        ariaLabel={m.route_admin_users_title()}
        loading={listLoading}
        data={items}
        rowKey={(u) => u.id}
        /*
         * T2A-F5: без `layout="fixed"` объявленные ширины колонок — не ширины, а
         * пожелания: браузер раздавал место по содержимому, и имя с ролью вставали
         * в две строки при пустых соседях (замер: имя 171px вместо объявленных 200,
         * роль 135 вместо 180). Ширины объявлены у всех колонок, поэтому
         * фиксированная раскладка здесь применима без оговорок.
         */
        layout="fixed"
        /*
         * Раунд 1 гейта T2A/SR, BLOCKER обеих половин: «колонка действий обрезана
         * краем таблицы — „Доступ“ рубится посередине слова». Реестр объявляет
         * семь колонок с фиксированными ширинами и при 1406px поля шире его —
         * прижимаем последнюю (действия), чтобы прокрутка не уносила кнопки.
         */
        stickyLastColumn
        columns={[
          {
            /**
             * Раунд 3 гейта T2A-F5, UX: у строк без отображаемого имени
             * единственным опознавательным знаком оставался адрес, а он обрезался
             * ровно на домене — `admin@demo.staiter…`, `outsider@staiter.te…`.
             * Прочерк вместо имени — не «нет данных», а «имя не заполнено», и
             * локальная часть адреса опознаёт человека лучше, чем тире.
             */
            id: 'full_name',
            header: m.admin_users_display_name(),
            /*
             * Раунд 3 гейта, UI (MAJOR): «squeeze переехал в имя: „e2e-admin имя не
             * ук…“ обрезается в строке со значком „Супер“». Ширина вернулась к
             * прежней и с запасом: остаток теперь забирает почта-поглотитель, и
             * платить за имя больше нечем.
             */
            // F19 круг 1 (обе половины): почта-ключ поиска резалась до 7 знаков при
            // 1218px фиксированных колонок из 1308. Ширины пересобраны под неё.
            // С-I2 Р.6: колонка действий выросла под «Вернуть в нашей компании», почта на 1280
            // не должна схлопнуться (замер: 1148 px таблицы, почте остаётся 130); 170 — заголовок
            // «Отображаемое имя» целиком.
            width: '170px',
            accessor: (u) => (
              /*
                Заход 2 гейта разрешал перенос, чтобы чип не разрывал имя. Раунд 8
                повторного гейта показал цену: с добавленной пометкой «имя не
                указано» строка `e2e-admin` уезжала на 70px против 49 у соседей.
                Ячейка держит ОДНУ линию, лишнее обрезается многоточием, а полное
                значение остаётся в подсказке.
              */
              <span className={styles.nameCell}>
                <EditableName
                  value={u.full_name ?? ''}
                  fallback={u.email}
                  readOnly={!caps.manageAccounts}
                  onSave={(fullName) => nameMut.mutate({ id: u.id, fullName })}
                />
                {/**
                 * F2 (service-role) — чип рядом с ИМЕНЕМ, а не под адресом.
                 *
                 * В ячейке почты он вытеснял адрес на вторую строку — находка
                 * дизайн-ревью. Тип субъекта относится к субъекту, а не к его
                 * почтовому адресу, и стоять должен там же, где имя.
                 */}
                {u.role === 'service' && (
                  <span className={styles.serviceTag} title={m.service_account_badge_title()}>
                    {/* Раунд 11 гейта T2A-F5: у соседнего чипа «Супер» значок есть, у
                        «Машины» не было — §4.4 требует «иконка + текст» для обоих. */}
                    <Bot size={12} aria-hidden="true" />
                    {m.service_account_badge()}
                  </span>
                )}
                {u.is_super_admin && (
                  <span
                    title={m.admin_users_super()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      padding: '1px 8px',
                      borderRadius: 'var(--radius-pill, 999px)',
                      // Заход 3 гейта, замер UI-ревьюера: `--warn` кеглем 10px на
                      // `--warn-bg-15` даёт 4,24:1 — комбинация, которую `tokens.css`
                      // помечает как провал AA. Текстовый тон и штатный мелкий кегль.
                      fontSize: 'var(--text-xs, 11px)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      color: 'var(--warn-text)',
                      background:
                        'var(--warn-bg-15, color-mix(in oklch, var(--warn), transparent 85%))',
                    }}
                  >
                    <ShieldCheck size={11} /> {m.ui_super()}
                  </span>
                )}
              </span>
            ),
          },
          {
            id: 'email',
            header: m.route_admin_users_col_email(),
            /**
             * Раунд 2 гейта, обе половины независимо: «почта — идентифицирующее
             * поле реестра — теперь обрезается в четырёх строках из пятнадцати,
             * тогда как справа от кнопок действий простаивает около 185px
             * пустоты» (UI); «реестр truncates email … при том что email здесь
             * единственный ключ различения (две строки „B4“ неотличимы)» (UX).
             *
             * Причина не в числе пикселей, а в том, что ВСЕ колонки объявлены
             * константами: остаток поля уходил в невидимый поглотитель справа.
             * Теперь остаток забирает почта — ширины у неё нет вовсе, и она
             * растёт с полем вместо того, чтобы резать адрес при простое рядом.
             */
            grow: true,
            /**
             * F2 (service-role) — сервисный аккаунт помечен ЯВНО.
             *
             * Он неотличим от сотрудника в списке: та же строка, тот же вид. Пометка
             * идёт по РОЛИ из ответа сервера, а не по маске почты — маска ломается на
             * первом же аккаунте, названном иначе.
             */
            // Круг 2 гейта F19 (обе половины): адрес резался и при 192px. Моно шире
            // шрифта интерфейса примерно на пятую часть, а почта — не величина
            // (§2.1: моно — количествам и кодам); полный адрес — в подсказке.
            // F106 (С-I, T2A-F24): почта не режется многоточием — длинный адрес переносится (ячейки
            // фиксированной таблицы по умолчанию `nowrap` + `ellipsis`).
            accessor: (u) => (
              <span className={styles.emailCell} title={u.email}>
                <EmailWrap text={u.email} />
              </span>
            ),
          },
          {
            /**
             * Раунд 1 повторного гейта `service-role` (T2A-F5), 🔴 BLOCKER обеих
             * половин: строка держалась не текстом, а КОНТРОЛАМИ — селект роли,
             * поле ставки и селект валюты в каждой строке давали 66px при спеке
             * 40/32, и от их ширины таблица переставала помещаться в окно.
             *
             * Реестр вернулся к своему делу — показывать, кто есть кто. Правка
             * роли и ставки живёт в карточке сотрудника («Изменить»), где для неё
             * есть место и подпись.
             *
             * Побочно закрыта находка UX той же пары: «у двух строк „Роль“
             * показывает „—“, тогда как „Доступы“ называют „Руководитель
             * компании“ и „Руководитель проекта“ — селект не умеет выразить эти
             * роли и молча врёт». Селекта нет — врать нечем: подпись берётся из
             * того же `getRoleLabel`, что и в колонке доступов.
             */
            id: 'role',
            // С-I7, круг 1 гейта (UX-4): правило колонки — подсказкой на заголовке, как у «Активность».
            header: (
              <span
                className={styles.headerWithHint}
                title={
                  registryFocus
                    ? m.route_admin_users_role_rule_company()
                    : m.route_admin_users_role_rule_aggregate()
                }
              >
                {m.route_admin_users_col_role()}
                <Info size={14} aria-hidden="true" />
              </span>
            ),
            // F106 (С-I, T2A-F24): ширины колонок ужаты под почту — она не режется. С-I2 Р.6 и Р.10: 122 —
            // самое длинное слово роли («Руководитель») влезает, роль переносится по словам; отданное —
            // колонке действий и бейджу «Доступ», почте остаётся прежняя ширина.
            width: '122px',
            // F106: длинная роль («Руководитель производства») переносится, а не режется.
            // С-I7 Р.4 (ACC-F9): роль членства — в компании охвата (или отбора «Компания»); в «Все
            // компании» и в холдинге — одна роль по компаниям строки или «разные роли» с перечнем.
            accessor: (u) => {
              const r = registryRole(u, registryCtx);
              if (r.kind === 'none') {
                return (
                  <span
                    className={styles.wrapCell}
                    {...(registryFocus ? { title: m.route_admin_users_role_not_member() } : {})}
                  >
                    —
                  </span>
                );
              }
              if (r.kind === 'role') {
                return (
                  <span className={styles.wrapCell} title={getRoleLabel(r.role)}>
                    {getRoleLabel(r.role)}
                  </span>
                );
              }
              // С-I7, круг 1 гейта (UI-5): по строке на компанию, пара «компания — роль» не рвётся
              // (неразрывные пробелы внутри пары).
              const list = r.companies
                .map((x) =>
                  m
                    .route_admin_users_role_mixed_item({
                      company: x.name ?? x.entityId.slice(0, 8),
                      role: getRoleLabel(x.role),
                    })
                    .replace(/ /g, ' '),
                )
                .join('\n');
              // Перечень по компаниям — подсказкой, которая видна и с клавиатуры (`title` не
              // показывается ни фокусом, ни на сенсорном экране).
              return (
                <Tooltip
                  content={m.route_admin_users_role_mixed_title({ list })}
                  placement="bottom"
                  multiline
                >
                  {/* biome-ignore lint/a11y/noNoninteractiveTabindex: фокус нужен, чтобы открыть перечень с клавиатуры */}
                  <span className={styles.roleMixed} tabIndex={0}>
                    {m.route_admin_users_role_mixed()}
                  </span>
                </Tooltip>
              );
            },
          },
          {
            /**
             * Раунд 5 гейта T2A-F5, обе половины и уже второй раунд подряд: три
             * колонки-прочерка («Сброс пароля», «Ставка / час», «Последний вход»)
             * держали около трёхсот пикселей, а имя, роль и адрес в это время
             * резались многоточием. UX назвала цену прямо: «при соседстве „QA
             * Руководитель проектов“ и „QA project_manager“ адрес — единственный
             * различитель».
             *
             * Две из трёх убраны из реестра совсем и живут в карточке сотрудника:
             * «Ставка / час» там уже редактируется, «Сброс пароля» — флаг, который
             * читают, открыв человека, а не сканируя список. «Последний вход»
             * оставлен: он отвечает (плохо, см. строку книги SR-F2) на вопрос
             * живости аккаунта, и это вопрос уровня списка.
             */
            id: 'active',
            // F19 круг 1 (UX, MAJOR): одно понятие — одно слово. Колонка, отбор и
            // значения говорят «Доступ: включён / отключён».
            header: m.route_admin_users_filter_active(),
            // F106 (С-I, NC-53): под бейджем «Нет» — строка «что · когда · кем»; место отдано колонкой действий.
            // С-I2 Р.10: 190 — бейдж «Отключён в ТОО Тест» (165 px) целиком; длинное имя компании переносится.
            width: '190px',
            /**
             * Раунд 8 гейта T2A-F5, UI: статус стоял голым «Да» — §4.4 STAITER_UI
             * требует бейдж со значком, а не только текст, и «статус дублируется
             * иконкой». Рядом при этом жил чип «Прямой», то есть акцент лежал на
             * происхождении записи, а не на её состоянии.
             *
             * Тон берётся из карты бейджа, подпись — доменная: «Активен» /
             * «Отключён», а не словарь состояний прогона агента.
             */
            accessor: (u) =>
              /*
                Раунд 9, обе половины: тринадцать зелёных «Да» подряд — цвет там,
                где нет отклонения. Активность это норма, и `neutral` для неё —
                прямое правило §1.3 («Baseline — нейтральный»); цвет остаётся за
                отключённым, ради которого колонка и существует.
              */
              /*
                Раунд 6, UI (MAJOR, второй раз): «„Активен: ○ Да“ — пустой кружок
                читается как невыбранный radio при тексте „Да“».
                Нейтральный бейдж нёс глиф-кольцо, которое статуса не значит.
                Норма подаётся текстом, бейдж остаётся ЗА ОТКЛОНЕНИЕМ — это то же
                правило §1.3, ради которого прошлый гейт увёл «Да» из зелёного в
                нейтральное; шаг доведён до конца.
              */
              !isRowDisabled(u) ? (
                <span>{m.route_admin_users_filter_active_yes()}</span>
              ) : (
                /*
                  F19 круг 1 (UX, MAJOR): «„Нет“ с перечёркнутым кругом визуально
                  равно failed/blocked, но это осознанное действие администратора».
                  Тон — завершённого исхода (тот же, что «Отменён» у проекта): форма
                  бейджа всё ещё отличает исключение от нормы-текста (SR-F6), а
                  красный остаётся за молчащей интеграцией в соседней колонке.

                  F106 (С-I, NC-53): под бейджем — что отключено, когда и кем. Имя
                  отключившего — только если он виден смотрящему (B83, К5-7), иначе
                  «администратор вне вашей компании»; без отметки — «дата неизвестна».
                */
                <span className={styles.disabledCell}>
                  {/* С-I2 Р.10, круг 1 (UI и UX): объём отключения — в самом бейдже, от него зависит кнопка
                      (В-48): «Аккаунт отключён» или «Отключён в <компании>». */}
                  <StatusBadge
                    status="terminated"
                    size="md"
                    className={styles.disabledChip}
                    label={
                      u.disabled?.scope === 'membership'
                        ? m.route_admin_users_disabled_chip_membership({
                            company:
                              (entitiesForTitle.data?.items ?? []).find(
                                (e) => e.id === u.disabled?.entity_id,
                              )?.name ?? m.route_admin_users_disabled_membership(),
                          })
                        : u.disabled?.scope === 'account' || !u.is_active
                          ? m.route_admin_users_disabled_chip_account()
                          : m.route_admin_users_filter_active_no()
                    }
                  />
                  {u.disabled && (
                    <span
                      className={styles.disabledMeta}
                      title={
                        u.disabled.scope === 'account'
                          ? m.route_admin_users_disabled_title_account()
                          : m.route_admin_users_disabled_title_membership()
                      }
                    >
                      {/* Строка «что · когда», под ней — кем. Дата — тем же форматом, что «Активность»
                          (круг 1, UI); полная — в подсказке. */}
                      {/* С-I2 Р.10: «что» ушло в бейдж — строкой «когда», под ней «кем». Время не рвётся. */}
                      <span className={styles.disabledWhen}>
                        {u.disabled.at ? (
                          <span title={notifTimeTitle(u.disabled.at, locale)}>
                            {notifTime(u.disabled.at, locale)}
                          </span>
                        ) : (
                          m.route_admin_users_disabled_unknown_date()
                        )}
                      </span>
                      {u.disabled.by && u.disabled.by.id === meId ? (
                        <span className={styles.disabledBy}>
                          {m.route_admin_users_disabled_by_you()}
                        </span>
                      ) : u.disabled.by ? (
                        <span className={styles.disabledBy}>
                          {/* Круг 1 (UI, MAJOR): адрес переносится только перед «@», не по дефису. */}
                          {m.route_admin_users_disabled_by_name()}{' '}
                          <EmailWrap text={u.disabled.by.name ?? u.disabled.by.id.slice(0, 8)} />
                        </span>
                      ) : u.disabled.by_hidden ? (
                        <span className={styles.disabledBy}>
                          {/* С-I2 Р.6: невидимый отключивший — ролью (по активным членствам). */}
                          {u.disabled.by_hidden === 'platform_admin'
                            ? m.route_admin_users_disabled_by_platform_line()
                            : m.route_admin_users_disabled_by_other_company_line()}
                        </span>
                      ) : null}
                    </span>
                  )}
                  {/* С-I8, гейт круги 1–2 (UX-2, ACC-F38): руководителю компании — кто вернёт аккаунт, здесь, под
                      «Аккаунт отключён», а не под кнопкой «Компании и роли» (замок под кнопкой — про неё саму). */}
                  {!caps.manageAccounts && isRestoreLocked(u, isSuper) && (
                    <span className={styles.restoreWhoCell}>
                      <Lock size={12} aria-hidden="true" />
                      {m.route_admin_users_restore_by_platform()}
                    </span>
                  )}
                </span>
              ),
          },
          {
            id: 'last_login',
            /**
             * Раунд 5 гейта, UX (MAJOR): «правило со экрана не выводится» — почему
             * у машины отсутствие вызовов красное, а у человека серое. Правило
             * объявлено подсказкой на самом заголовке, а не оставлено догадке.
             */
            header: (
              /*
                Раунд 6, UX: «заголовок не несёт ни значка ⓘ, ни пунктирного
                подчёркивания, поэтому наводить на него незачем и никто не
                наведёт». Подсказка обязана висеть на ВИДИМОМ носителе.
              */
              <span className={styles.headerWithHint} title={m.route_admin_users_activity_rule()}>
                {m.route_admin_users_col_last_activity()}
                <Info size={14} aria-hidden="true" />
              </span>
            ),
            /*
              Раунд 7, обе половины: «заголовок говорит только за машины —
              „Последний вызов API“ стоит над строками, где человек читает „ещё не
              входил“». Имя колонки стало нейтральным, а ИСТОЧНИК величины (вход у
              человека, вызов у машины) назван в подсказке. 195, а не 175: значок
              подсказки сжимался до нуля, потому что подпись занимала всю ширину.
            */
            // Круг 3 гейта F19: подпись «Последняя активность» держала 210px ради
            // заголовка при значениях в 90px («3 ч назад») — и отнимала их у имени и
            // роли. Заголовок — «Активность», источник величины называет подсказка.
            // F98 (С-I, NC-70): малый бейдж стал 12 px (был 11) — «не обращался» шириной 112 px не
            // влезал в 130 − 24 px отступов и обрезался многоточием. 140 px — замер, не на глаз.
            width: '140px',
            /**
             * T2A-F3 / SR-F2 (заход 7) — колонка наконец отвечает на свой вопрос.
             *
             * Было «Последний вход» — и оно показывало «никогда» ВСЕМ, включая
             * работающих каждый день: поля за колонкой не существовало вовсе
             * (`last_login_at` объявлен необязательным в контракте и не приходил
             * никогда). У сервисного аккаунта прочерк объяснялся закрытым входом
             * в интерфейс, и объяснение было верным, но частным: мёртвой колонка
             * была для всех.
             *
             * Стало «Последняя активность» — отметка последнего вызова API
             * (migration 0119). Один вопрос, один ответ, одинаково честный для
             * человека и для машины; для сервисного аккаунта это и есть
             * единственный способ узнать, жива ли интеграция.
             */
            accessor: (u) => {
              const ts = u.last_api_call_at ?? null;
              /**
               * Раунд 1 гейта, UX-половина, BLOCKER: «колонка одинаково нейтрально
               * рисует „9 мин назад“ и молчащую неделю интеграцию; degraded не
               * отличим от healthy».
               *
               * Порог ставится ТОЛЬКО сервисному аккаунту, и это не экономия, а
               * правило §1.3 «цвет только при отклонении»: у человека месяц без
               * входа — обычный отпуск, у интеграции сутки молчания — инцидент.
               * Одна колонка, две популяции, суждение выносится там, где для него
               * есть основание.
               */
              const isMachine = u.role === 'service';
              const silentMs = ts ? Date.now() - new Date(ts).getTime() : Number.POSITIVE_INFINITY;
              const machineSilent = isMachine && silentMs > 24 * 3600 * 1000;
              if (!ts) {
                /**
                 * Раунды 3 и 6 гейта разошлись по одному месту, и обе стороны
                 * правы по-своему.
                 *
                 * Раунд 3 (UX): «„Не обращался“ (красный) и „никогда“ (серый)
                 * означают ровно одно» — два слова на один факт.
                 * Раунд 6 (обе половины): «одна и та же фраза означает тревогу у
                 * машины и норму у человека … читается как случайность разметки»
                 * — одно слово на два разных смысла.
                 *
                 * Разрешение: слова РАЗНЫЕ, потому что и факты разные. У машины
                 * «не обращался» — интеграция не работала ни разу, это инцидент.
                 * У человека «ещё не входил» — он просто не начинал, это норма.
                 * Тон следует за смыслом, а не заменяет его.
                 */
                return isMachine ? (
                  <StatusBadge
                    status="blocked"
                    size="sm"
                    label={m.route_admin_users_activity_never()}
                  />
                ) : (
                  <span
                    className={styles.mutedCell}
                    title={m.route_admin_users_last_activity_hint()}
                  >
                    {m.route_admin_users_activity_no_login()}
                  </span>
                );
              }
              if (machineSilent) {
                return (
                  <span
                    title={`${notifTimeTitle(ts, locale)} · ${m.route_admin_users_last_activity_hint()}`}
                  >
                    <StatusBadge
                      status="needs_review"
                      size="sm"
                      /*
                        Раунд 3: подпись — ДЛИТЕЛЬНОСТЬ, а не дата. «молчит 25 авг.,
                        12:34» отвечало на вопрос «когда», при том что колонка
                        спрашивает «сколько уже»; точная метка осталась в подсказке.
                      */
                      label={m.route_admin_users_activity_silent_days({
                        days: Math.floor(silentMs / (24 * 3600 * 1000)),
                      })}
                    />
                  </span>
                );
              }
              /**
               * Относительное время, а не «31.08.2026, 09:52».
               *
               * Вопрос колонки — «жив ли аккаунт», и ответом на него является
               * ВОЗРАСТ отметки, а не её календарная позиция: «17 дней назад»
               * говорит про интеграцию всё, «14.08.2026, 11:53» требует считать
               * в уме. Заодно снимается обрезка: полная дата со временем в
               * 175 px не помещалась и рисовалась как «31.08.2026, 09:5…».
               *
               * `notifTime` — тот же формат, что в центре уведомлений, вместе с
               * его правилом «до суток относительно, дальше абсолютная дата»:
               * оно заведено ровно против ошибки «застарелое читается как
               * свежее». Точная метка — в подсказке.
               */
              return (
                <span
                  className={styles.tabularCell}
                  title={`${notifTimeTitle(ts, locale)} · ${m.route_admin_users_last_activity_hint()}`}
                >
                  {notifTime(ts, locale)}
                </span>
              );
            },
          },
          {
            id: 'memberships',
            // F19 круг 1 (UX, MAJOR) + SR-F7/SR-F8 (F21): колонка перечисляет компании —
            // «Компании»; «Доступы» остаётся за кнопкой перехода к правам.
            // SR-F7 / SR-F11 (F21): правило выбора видимой компании и смысл точки-признака
            // названы на видимом носителе — значке подсказки у заголовка.
            header: (
              <span className={styles.headerWithHint} title={m.route_admin_users_companies_rule()}>
                {m.route_admin_users_col_companies()}
                <Info size={14} aria-hidden="true" />
              </span>
            ),
            /*
              Раунд 7, UX: «усечение „ТОО Бета · Б…“ в колонке доступов». Ширину
              вернули: остаток поля теперь забирает почта-поглотитель, и платить
              за чипы компаний больше нечем.
            */
            // F27 круг 1 (обе половины): при 100px обрезались значок заголовка и чип
            // («ТО…»), а с ним точка SR-F11.
            width: '140px',
            accessor: (u) => {
              // BM-3-10/FM-4-16/FM-6-4: backend возвращает entity_memberships[] денормом
              // с entity_name (BM-6-4 leftJoin). Pill = «Компания · Роль».
              const arr = (
                u as unknown as {
                  entity_memberships?: Array<{
                    entity_id: string;
                    entity_name?: string | null;
                    role: string;
                    status: string;
                  }>;
                }
              ).entity_memberships;
              if (!arr || arr.length === 0)
                return <span style={{ color: 'var(--text-muted)' }}>—</span>;
              // SR-F7: «ТОО Тест +2» читалось «основным местом работы», хотя это была
              // первая запись. Первой идёт компания активного контекста.
              const ordered = activeEntityId
                ? [...arr].sort(
                    (a, b) =>
                      Number(b.entity_id === activeEntityId) -
                      Number(a.entity_id === activeEntityId),
                  )
                : arr;
              /*
               * Раунд 2 гейта T2A-F5, обе половины: чип обрывался посреди слова
               * («Руководитель проекто») вместе с правой рамкой — без многоточия и
               * без признака, что текст продолжается. Обрезка честная: многоточие
               * внутри чипа плюс подсказка с полным значением, которая тут уже была.
               */
              return (
                <div className={styles.memberChips}>
                  {ordered.slice(0, 1).map((mem) => (
                    <span
                      key={mem.entity_id}
                      className={styles.memberChip}
                      title={`${mem.entity_name ?? mem.entity_id} · ${getRoleLabel(mem.role)}`}
                    >
                      {/*
                        Раунд 6 гейта T2A-F5 убрал из чипа роль там, где она
                        совпадает с соседней колонкой, и оставил её там, где
                        расходится. Раунд 8 гейта T2A/SR показал цену этого
                        решения: «чип всё ещё обрезается — „ТОО Бета · Бухга…“ …
                        185px не вмещают форму „имя · роль“» и «одна колонка — две
                        формы записи … столбец выглядит inconsistent».

                        Оба раунда правы, и оба требования совместимы: имя
                        компании стоит ЦЕЛИКОМ и всегда в одной форме, роль — в
                        подсказке. С-I7 Р.4 (ACC-F9): признак «роль расходится с
                        базовой» снят — колонка «Роль» сама показывает роль
                        членства, базовая роль — только в карточке суперадмину.
                      */}
                      {mem.entity_name ?? '—'}
                    </span>
                  ))}
                  {/*
                    Раунд 7 гейта T2A-F5: два чипа не помещались в одну линию и
                    переносились, разгоняя строку до 78px. Реестр показывает ОДНУ
                    компанию и счётчик остальных — полный перечень в подсказке, а
                    сам разбор доступа живёт на своём экране, кнопка в двух ячейках
                    правее.
                  */}
                  {arr.length > 1 && (
                    <span
                      className={styles.memberMore}
                      /* Счётчик считает ОСТАЛЬНЫХ — их и перечисляет (раунд 8, MINOR). */
                      title={ordered
                        .slice(1)
                        .map((x) => x.entity_name ?? x.entity_id)
                        .join(', ')}
                    >
                      +{arr.length - 1}
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            /**
             * T2A-F2 / SR-F1(б) — строка реестра доросла до 182px при спеке 40/32, и
             * колонка действий уезжала за правый край: пять кнопок в ячейке с
             * `flex-wrap` вставали в пять этажей, а таблица от этого расширялась и
             * обрезалась. Замер до правки: высота строк [182, 182, 182, 146, 182, 182].
             *
             * Причина не в вёрстке ячейки, а в её составе: реестр показывал ВСЕ
             * действия сразу, включая редкие и опасные («Войти от имени»). Два
             * ежедневных хода остаются кнопками, остальные уходят в меню строки —
             * ровно тот приём, которым уже живёт реестр проектов
             * (`routes/_auth.projects.index.tsx`), и заводить второй способ ради
             * одного экрана не за что.
             */
            id: 'actions',
            header: '',
            // F106 (С-I, T2A-F24): одна кнопка + меню — колонка уже не плотнее данных, место ушло почте
            // и строке отключения. С-I2 Р.6: шире под «Вернуть в нашей компании» (200 px резали подпись);
            // круг 1 гейта Р.10 (UI, BLOCKER): кнопки без значков — 248 резали её слева. Замер: самая
            // длинная кнопка + 8 + «…» 32 + отступы ячейки 24 ≤ 256.
            width: '256px',
            accessor: (u) => (
              <div className={styles.actionsStack}>
                <div className={styles.actionsCell}>
                  {rowActions(u).primary === 'access' ? (
                    /* С-I8, гейт круг 1 (UX-1, ACC-F37): доступы человека с ролью выше своей руководитель
                       компании не меняет — кнопка на месте, неактивна, причина строкой ниже. */
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={rowActions(u).accessLocked}
                      focusableWhenDisabled={rowActions(u).accessLocked}
                      {...(rowActions(u).accessLocked
                        ? { 'aria-describedby': `access-locked-${u.id}` }
                        : {})}
                      onClick={() => openAccess(u)}
                    >
                      {m.route_admin_users_menu_access()}
                    </Button>
                  ) : isRowDisabled(u) ? (
                    /* Круг 2 гейта F19 (UX, MAJOR): у отключённого следующий шаг — вернуть доступ. */
                    /* Круг 1 (UX, MAJOR): отключение, поставленное невидимым смотрящему администратором, админ
                     компании не снимает — кнопка на месте, неактивна, причина в подсказке и для чтеца;
                     возврат аккаунта — через подтверждение объёма («во все компании»). */
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={restoreMut.isPending || isRestoreLocked(u, isSuper)}
                      focusableWhenDisabled={isRestoreLocked(u, isSuper)}
                      {...(isRestoreLocked(u, isSuper)
                        ? { 'aria-describedby': `restore-locked-${u.id}` }
                        : {})}
                      onClick={() =>
                        restoreScope(u) === 'account' ? setConfirmRestore(u) : restoreMut.mutate(u)
                      }
                    >
                      {restoreScope(u) === 'membership'
                        ? m.route_admin_users_restore_in_company()
                        : m.route_admin_users_restore_access()}
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setEditingUser(u)}>
                      {m.ui_izmenit()}
                    </Button>
                  )}
                  {rowActions(u).menu.length > 0 && (
                    <button
                      type="button"
                      className={styles.rowMenuBtn}
                      aria-label={`${m.project_header_more_actions()} · ${u.full_name ?? u.email}`}
                      title={m.project_header_more_actions()}
                      onClick={(e) => {
                        e.stopPropagation();
                        openContextMenuFromButton(e.currentTarget, buildUserRowMenu(u));
                      }}
                    >
                      <MoreHorizontal size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {/* С-I2 Р.10 (NC-131), круг 1 (UI MAJOR, UX MINOR): кто может вернуть — под самой выключенной
                  кнопкой, своей строкой со значком, в другой колонке, чем «кто отключил»; она же — описание
                  кнопки (`aria-describedby`). Ячейка «Доступ» больше не растёт до семи строк. */}
                {caps.manageAccounts && isRowDisabled(u) && isRestoreLocked(u, isSuper) && (
                  <span id={`restore-locked-${u.id}`} className={styles.restoreWho}>
                    <Lock size={12} aria-hidden="true" />
                    {m.route_admin_users_restore_locked_short()}
                  </span>
                )}
                {rowActions(u).accessLocked && (
                  <span id={`access-locked-${u.id}`} className={styles.restoreWho}>
                    <Lock size={12} aria-hidden="true" />
                    {m.route_admin_users_access_locked_short()}
                  </span>
                )}
              </div>
            ),
          },
        ]}
      />

      <ConfirmDialog
        open={confirmRestore !== null}
        title={m.route_admin_users_restore_account_title()}
        description={m.route_admin_users_restore_account_body({
          name: confirmRestore?.full_name ?? confirmRestore?.email ?? '',
        })}
        confirmLabel={m.route_admin_users_restore_access()}
        onConfirm={() => {
          if (confirmRestore) restoreMut.mutate(confirmRestore);
          setConfirmRestore(null);
        }}
        onCancel={() => setConfirmRestore(null)}
      />
      {editingUser && (
        <UserEditDrawer
          user={editingUser}
          open={editingUser !== null}
          onClose={() => setEditingUser(null)}
        />
      )}

      <InvitesSection filterActive={filterActive} canRevoke={caps.revokeInvite} />

      <InviteDialog
        open={inviteOpen}
        isSuper={isSuper}
        knownUsers={(data?.items ?? []) as AdminUser[]}
        onOpenUser={(u) => {
          setInviteOpen(false);
          setEditingUser(u);
        }}
        onClose={() => setInviteOpen(false)}
        onCreated={() => {
          setInviteOpen(false);
          void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
        }}
      />

      <BulkInviteDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
        }}
      />

      <Dialog
        open={!!confirmRotate}
        onClose={() => setConfirmRotate(null)}
        title={m.service_account_rotate_confirm_title()}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRotate(null)}>
              {m.common_button_cancel()}
            </Button>
            <Button
              variant="destructive"
              loading={rotateMut.isPending}
              onClick={() => confirmRotate && rotateMut.mutate(confirmRotate.id)}
            >
              {m.service_account_rotate_confirm_cta()}
            </Button>
          </>
        }
      >
        <p>{m.service_account_rotate_confirm_body()}</p>
        <p className={shared.subtle}>{confirmRotate?.email}</p>
      </Dialog>

      <Dialog
        open={!!rotatedSecret}
        onClose={() => setRotatedSecret(null)}
        title={m.service_account_rotate_title()}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRotatedSecret(null)}>
              {m.common_button_close()}
            </Button>
            {/*
              Заход 4 гейта: главный слот отдан ЕДИНСТВЕННОМУ действию, которое
              сохраняет работоспособность интеграции. Секрет больше не покажут, и
              заливная «Закрыть» была самой заметной кнопкой, теряющей его.
            */}
            <Button
              variant="primary"
              onClick={() => {
                if (rotatedSecret) void navigator.clipboard?.writeText(rotatedSecret.secret);
                toast.push({ variant: 'success', title: m.service_account_rotate_copied() });
              }}
            >
              {m.service_account_rotate_copy()}
            </Button>
          </>
        }
      >
        <p>{m.service_account_rotate_body()}</p>
        {/* Секрет — артефакт, а не проза: собственный блок кода, выделяемый целиком. */}
        <code className={styles.secretBlock}>{rotatedSecret?.secret}</code>
        <p className={shared.subtle}>
          {m.service_account_rotate_revoked({ count: rotatedSecret?.revoked ?? 0 })}
        </p>
      </Dialog>

      <Dialog
        open={!!confirmImpersonate}
        onClose={() => setConfirmImpersonate(null)}
        title={m.route_admin_users_impersonate_title()}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmImpersonate(null)}>
              {m.common_button_cancel()}
            </Button>
            <Button
              onClick={() => confirmImpersonate && imperMut.mutate(confirmImpersonate.id)}
              disabled={imperMut.isPending}
            >
              {imperMut.isPending
                ? m.route_admin_users_impersonate_launching()
                : m.route_admin_users_impersonate_confirm()}
            </Button>
          </>
        }
      >
        <p>
          {m.route_admin_users_impersonate_warning_prefix()}{' '}
          <strong>{confirmImpersonate?.email}</strong>
          {m.route_admin_users_impersonate_warning_suffix()}
        </p>
      </Dialog>
    </div>
  );
}

/**
 * `adjustedFrom` — С-I7 Р.5 (ACC-F10): роль строки сменилась сама при смене компании (прежняя там
 * недоступна) — под строкой подсказка, что роль изменилась.
 */
type InviteMembershipRow = {
  uid: number;
  entity_id: string;
  role: Role;
  adjustedFrom?: Role | undefined;
};
let inviteRowUid = 0;
const nextInviteRow = (): InviteMembershipRow => ({
  uid: ++inviteRowUid,
  entity_id: '',
  role: 'accountant',
});

function InviteDialog({
  open,
  isSuper,
  knownUsers,
  onClose,
  onCreated,
  onOpenUser,
}: {
  open: boolean;
  isSuper: boolean;
  /** Люди реестра — отличить «уже состоит в этой компании» от «есть в другой» при `EMAIL_EXISTS`. */
  knownUsers: AdminUser[];
  onClose: () => void;
  onCreated: () => void;
  onOpenUser: (u: AdminUser) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  // FM-S-multi-6-2: target-выбор для super_admin (компания / холдинг).
  const [targetKind, setTargetKind] = useState<'entity' | 'holding'>('entity');
  // invite-multi-entity — строки «компания + роль в ней»; первая = primary
  // (default-компания приглашённого). Компания всегда явная — legacy-инвайт
  // «в компанию из JWT» отправил пользователя в сид-компанию (инцидент 2026-07-13).
  const [rows, setRows] = useState<InviteMembershipRow[]>(() => [nextInviteRow()]);
  const [holdingId, setHoldingId] = useState('');
  const [holdingRole, setHoldingRole] = useState<'holding_admin' | 'holding_viewer'>(
    'holding_viewer',
  );
  // С-I6 Р.2 (ACC-B22) — компании и роли от того же правила, что проверяет приглашение на сервере:
  // только компании, где приглашающий администрирует, и роли не выше его роли в выбранной компании.
  const optionsQ = useQuery({
    queryKey: ['admin', 'invite-options'],
    queryFn: fetchInviteOptions,
    enabled: open,
  });
  const holdingsQ = useHoldings();
  const toast = useToast();
  const qcInvite = useQueryClient();

  /*
   * С-I7 Р.5 (ACC-F10): загрузка и ошибка — свои состояния окна (`InviteOptionsStatus`), форма до
   * ответа не активна. Роли — по рангу, как отдаёт сервер (старшая сверху), — `rolesFor`.
   */
  const view = inviteOptionsView(optionsQ);
  const entityOptions = view.state === 'ready' ? view.entities : [];
  const roleOrder = view.state === 'ready' ? view.order : [];
  const noCompanies = !isSuper && view.state === 'ready' && entityOptions.length === 0;
  const rowRoles = (entityId: string): Role[] => rolesFor(entityId, entityOptions);
  // Единственная доступная компания — подставляем сразу, выбора нет.
  useEffect(() => {
    const ready = inviteOptionsView({ isError: false, data: optionsQ.data });
    if (ready.state !== 'ready' || ready.entities.length !== 1) return;
    const only = ready.entities[0]!;
    const allowed = rolesFor(only.id, ready.entities);
    setRows((prev) =>
      prev.length === 1 && prev[0]!.entity_id === ''
        ? [
            {
              ...prev[0]!,
              entity_id: only.id,
              role: fitInviteRole(prev[0]!.role, allowed, ready.order),
            },
          ]
        : prev,
    );
  }, [optionsQ.data]);

  /*
   * С-I7 Р.5 (ACC-F10): отказ сервера — строкой в самом окне. Тост ложился под подложку модального окна
   * (слой тостов ниже верхнего слоя `<dialog>`), и отказ «нет права приглашать» / «уже есть» не был виден.
   * Строка гаснет при любой правке формы.
   */
  const [submitError, setSubmitError] = useState<{ text: string; user?: AdminUser } | null>(null);

  const reset = (): void => {
    setEmail('');
    setRows([nextInviteRow()]);
    setHoldingId('');
    setSubmitError(null);
  };

  const updateRow = (idx: number, patch: Partial<InviteMembershipRow>): void => {
    setSubmitError(null);
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const mut = useMutation({
    // С-I6 Р.2 — отказ показывает свой тост текстом по коду; общий тост дублировал его словами «нет прав».
    meta: { suppressErrorToast: true },
    mutationFn: () => {
      if (isSuper && targetKind === 'holding') {
        return inviteUserTarget({
          email,
          target: { type: 'holding', holding_id: holdingId, role: holdingRole },
        });
      }
      return inviteUserMemberships({
        email,
        memberships: rows.map((r) => ({ entity_id: r.entity_id, role: r.role })),
      });
    },
    onSuccess: (resp) => {
      const target = email;
      reset();
      // С-H2 Р.6 — письмо уходит после коммита: `null` (в очереди) — успех, `false` — не ушло.
      if (resp.email_sent !== false) {
        // F109, круг 2 (UX-4): письмо ещё в пути — info, а не success.
        // С-I2 Р.8 (В-50): ссылка — один раз, в ответе выпуска; в тосте её можно скопировать.
        const link = resp.accept_url;
        // Круг 2 гейта Р.8 (UX-3): почта лежит — письмо не уйдёт, ссылка — единственный путь; так и сказано.
        const mailDown = Boolean(link) && inviteMailDown(qcInvite);
        toast.push({
          variant: mailDown ? 'warning' : 'info',
          title: mailDown
            ? m.admin_invite_toast_mail_down_title()
            : m.admin_invite_toast_sent_title(),
          description: mailDown
            ? m.admin_invite_toast_mail_down_body({ email: noBreakHyphen(target) })
            : link
              ? m.admin_invite_toast_sent_body_link({ email: noBreakHyphen(target) })
              : m.admin_invite_toast_sent_body({ email: noBreakHyphen(target) }),
          ...(link
            ? inviteLinkToastFields(link, toast.push)
            : { action: { label: m.admin_invite_toast_show(), onClick: scrollToInvites } }),
        });
      } else {
        // Круг 1 гейта Р.8: письмо не ушло — ссылка тем нужнее; та же форма, что у успеха.
        toast.push({
          variant: 'warning',
          title: m.admin_invite_toast_email_failed_title(),
          // Круг 2 (UX-2): при ссылке — «передайте сами», а не «Отправить снова» (он гасит эту ссылку).
          description: resp.accept_url
            ? m.admin_invite_toast_email_failed_body_link({ email: noBreakHyphen(target) })
            : m.admin_invite_toast_email_failed_body({ email: noBreakHyphen(target) }),
          ...(resp.accept_url
            ? inviteLinkToastFields(resp.accept_url, toast.push)
            : { durationMs: 10000 }),
        });
      }
      onCreated();
    },
    // С-I6 Р.2 — отказ сервера текстом по коду (`details.code`), а не пустым `message`; С-I7 Р.5 — в окне.
    onError: (err) => {
      // С-I7, круг 1 гейта (UX-3): человек уже в выбранной компании — не «добавить может
      // администратор его компании» (это сам приглашающий), а «уже состоит» и переход в карточку.
      const member =
        inviteErrorCode(err) === 'EMAIL_EXISTS'
          ? alreadyMemberIn(
              knownUsers,
              email,
              rows.map((r) => r.entity_id),
            )
          : null;
      setSubmitError(
        member
          ? {
              text: m.admin_invite_already_member({
                email: email.trim().replace(/-/g, '‑'),
                company: member.company,
              }),
              user: member.user,
            }
          : { text: inviteErrorText(err) },
      );
    },
  });

  const rowsValid =
    rows.length > 0 &&
    rows.every((r) => !!r.entity_id) &&
    new Set(rows.map((r) => r.entity_id)).size === rows.length;
  const targetValid = isSuper && targetKind === 'holding' ? !!holdingId : rowsValid;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={m.route_admin_users_invite_title()}
      size="md"
      footer={
        noCompanies || view.state !== 'ready' ? (
          // Круг 2 гейта С-I6 (UI и UX): приглашать некуда — без формы, которую нельзя отправить; одна
          // кнопка закрытия Secondary (как в массовом окне в том же состоянии). С-I7 Р.5: так же, пока
          // компании и роли грузятся или не загрузились.
          <Button variant="secondary" onClick={onClose}>
            {m.common_button_close()}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {m.common_button_cancel()}
            </Button>
            <Button
              onClick={() => mut.mutate()}
              // С-I7, круг 2 гейта (UX-4): человек уже в компании — повтор с той же почтой снова получит
              // отказ; отправка неактивна до правки почты, главное действие — «Открыть карточку».
              disabled={
                !email.includes('@') || !targetValid || mut.isPending || Boolean(submitError?.user)
              }
            >
              {m.route_admin_users_invite_submit()}
            </Button>
          </>
        )
      }
    >
      {view.state !== 'ready' && (
        <InviteOptionsStatus
          state={view.state}
          error={optionsQ.error}
          onRetry={() => void optionsQ.refetch()}
        />
      )}
      {view.state === 'ready' && !noCompanies && (
        <Input
          label={m.route_admin_users_col_email()}
          type="email"
          value={email}
          onChange={(e) => {
            setSubmitError(null);
            setEmail(e.target.value);
          }}
          placeholder="user@example.com"
        />
      )}

      {view.state === 'ready' && isSuper && (
        <div className={styles.sectionGap}>
          <Select
            label={m.route_admin_users_invite_target_label()}
            value={targetKind}
            onChange={(e) => setTargetKind(e.target.value as 'entity' | 'holding')}
            options={[
              { value: 'entity', label: m.route_admin_users_invite_target_entity() },
              { value: 'holding', label: m.route_admin_users_invite_target_holding() },
            ]}
          />
        </div>
      )}

      {view.state !== 'ready' ? null : isSuper && targetKind === 'holding' ? (
        <>
          <div className={styles.sectionGap}>
            <Select
              label={m.route_admin_users_invite_holding_label()}
              value={holdingId}
              onChange={(e) => setHoldingId(e.target.value)}
              options={[
                { value: '', label: m.route_admin_users_invite_holding_placeholder() },
                ...(holdingsQ.data?.items ?? []).map((h) => ({ value: h.id, label: h.name })),
              ]}
            />
          </div>
          <div className={styles.sectionGap}>
            <Select
              label={m.route_admin_users_col_role()}
              value={holdingRole}
              onChange={(e) => setHoldingRole(e.target.value as 'holding_admin' | 'holding_viewer')}
              options={[
                { value: 'holding_admin', label: getRoleLabel('holding_admin') },
                { value: 'holding_viewer', label: getRoleLabel('holding_viewer') },
              ]}
            />
          </div>
        </>
      ) : noCompanies ? (
        <p className={[styles.inviteRightsNote, styles.inviteRightsNoteFlush].join(' ')}>
          <Info size={14} aria-hidden="true" />
          {m.route_admin_users_invite_no_companies()}
        </p>
      ) : (
        <>
          {rows.map((row, idx) => {
            // Компании, уже выбранные в других строках, из options убираем —
            // одно членство на компанию.
            const usedElsewhere = new Set(rows.filter((_, i) => i !== idx).map((r) => r.entity_id));
            return (
              <div key={row.uid} className={styles.sectionGap}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      label={m.route_admin_users_invite_entity_label()}
                      value={row.entity_id}
                      onChange={(e) => {
                        // С-I7 Р.5 (ACC-F10): роль — ближайшая разрешённая не выше прежней; сменилась
                        // сама — подсказка под строкой.
                        const next = fitInviteRole(row.role, rowRoles(e.target.value), roleOrder);
                        updateRow(idx, {
                          entity_id: e.target.value,
                          role: next,
                          ...(next !== row.role
                            ? { adjustedFrom: row.role }
                            : { adjustedFrom: undefined }),
                        });
                      }}
                      // Круг 1 гейта С-I6 (UI, UX): пункта «Выберите компанию» в списке нет — его выбор
                      // возвращал форму в невалидное состояние. С-I7, круг 1 гейта (UI-3, UX-1): до
                      // выбора — приглушённая заглушка поля, а не пункт с галочкой.
                      placeholder={m.route_admin_users_invite_entity_placeholder()}
                      options={entityOptions
                        .filter((ent) => !usedElsewhere.has(ent.id))
                        .map((ent) => ({ value: ent.id, label: ent.name }))}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      label={m.route_admin_users_col_role()}
                      value={row.role}
                      onChange={(e) =>
                        updateRow(idx, { role: e.target.value as Role, adjustedFrom: undefined })
                      }
                      options={rowRoles(row.entity_id).map((r) => ({
                        value: r,
                        label: getRoleLabel(r),
                      }))}
                    />
                  </div>
                  {rows.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={m.route_admin_users_invite_remove_company()}
                      onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <XIcon size={14} />
                    </Button>
                  )}
                </div>
                {row.adjustedFrom && (
                  <p className={styles.inviteRoleChanged} role="status">
                    <Info size={14} aria-hidden="true" />
                    {m.route_admin_users_invite_role_changed({
                      from: getRoleLabel(row.adjustedFrom),
                      role: getRoleLabel(row.role),
                    })}
                  </p>
                )}
              </div>
            );
          })}
          {rows.length < entityOptions.length && (
            <div className={styles.sectionGap}>
              <Button
                variant="ghost"
                size="sm"
                // С-I7, круг 1 гейта (UX-5): у действия — значок, иначе оно читалось простым текстом.
                leadingIcon={<Plus size={14} aria-hidden="true" />}
                onClick={() => setRows((prev) => [...prev, nextInviteRow()])}
              >
                {m.route_admin_users_invite_add_company()}
              </Button>
            </div>
          )}
          {/* Круг 1 гейта С-I6 (UX, MAJOR): ограничение не молча — почему в списке не все компании и роли. */}
          {!isSuper && (
            <p className={styles.inviteRightsNote}>
              <Info size={14} aria-hidden="true" />
              {m.route_admin_users_invite_rights_hint()}
            </p>
          )}
        </>
      )}
      {submitError && (
        <div className={styles.inviteSubmitError} role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>
            <strong>{m.admin_invite_toast_error_title()}.</strong> {submitError.text}
          </span>
          {submitError.user && (
            <Button
              size="sm"
              variant="primary"
              className={styles.inviteSubmitErrorAction}
              onClick={() => {
                const u = submitError.user;
                if (u) onOpenUser(u);
              }}
            >
              {m.admin_invite_open_card()}
            </Button>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** oleAbove — С-I7, круг 2 гейта (UX-2): строку с ролью выше своей исправляют выбором роли прямо в предпросмотре. */
type ParsedRow = {
  email: string;
  role: Role;
  valid: boolean;
  reason?: string;
  roleAbove?: boolean;
};

function BulkInviteDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [raw, setRaw] = useState('');
  const [defaultRole, setDefaultRole] = useState<Role>('accountant');
  // Точечная правка роли конкретной строки прямо в превью (по email). Переживает
  // смену defaultRole и редактирование текста (для не затронутых адресов).
  const [roleOverrides, setRoleOverrides] = useState<Record<string, Role>>({});
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<BulkInviteResult | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /**
   * С-I2 Р.8, круг 1 гейта (UX, MAJOR): ссылки итога — одноразовые. Скопированные отмечаются, «Скопировать
   * все» кладёт пары «адрес — ссылка», а закрытие окна при нескопированных спрашивает подтверждение.
   */
  const [copied, setCopied] = useState<ReadonlySet<string>>(new Set());
  const [confirmClose, setConfirmClose] = useState(false);
  /**
   * Круг 2 гейта Р.8 (UI-1, MAJOR): окно модальное — тост ошибки под подложкой не выделить. Отказ буфера
   * показывает ссылку полем прямо в окне (у строки — её ссылку, у «Скопировать все» — пары).
   */
  const [copyFailed, setCopyFailed] = useState<ReadonlySet<string>>(new Set());
  const [copyAllFailed, setCopyAllFailed] = useState(false);
  // Сколько ссылок заменил повтор — строкой в окне: тост ложится под подложку модального окна.
  const [replacedLinks, setReplacedLinks] = useState(0);
  const [retryFailedCount, setRetryFailedCount] = useState(0);
  // Круг 4 (UI-2): поле отказа строки появилось — фокус и выделение в нём после отрисовки, остаётся Ctrl+C.
  const [focusLinkField, setFocusLinkField] = useState<string | null>(null);
  useEffect(() => {
    if (!focusLinkField) return;
    const field = document.querySelector<HTMLInputElement>(
      `input[data-link-field="${CSS.escape(focusLinkField)}"]`,
    );
    field?.focus();
    field?.select();
    setFocusLinkField(null);
  }, [focusLinkField]);
  // Фокус подтверждения — на «Скопировать все и закрыть»: Dialog сажал его на крестик.
  const copyAllCloseRef = useRef<HTMLButtonElement | null>(null);
  // С-I3 Р.2, круг 2 гейта (UI, MINOR): итог пришёл — фокус на «Скопировать все», а не на крестик.
  const copyAllRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!result) return undefined;
    const timer = setTimeout(() => copyAllRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [result]);
  useEffect(() => {
    if (!confirmClose) return undefined;
    const timer = setTimeout(() => copyAllCloseRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [confirmClose]);

  /*
   * С-I6 Р.2 (ACC-B22) — массовое окно шлёт старую форму `{email, role}`: приглашение идёт в компанию
   * запроса (активную). Роли — не выше своей там, по тому же правилу, что на сервере; нет права
   * приглашать в этой компании — строка с причиной и отправка закрыта.
   */
  const optionsQ = useQuery({
    queryKey: ['admin', 'invite-options'],
    queryFn: fetchInviteOptions,
    enabled: open,
  });
  /*
   * С-I7 Р.5 (ACC-F10): роли — только с сервера и в его порядке (по рангу); до ответа и при ошибке
   * ролей нет и форма не показывается (`InviteOptionsStatus`). Раньше до ответа подставлялись все
   * роли членства — и роль строки, выбранная в этот момент, уходила на сервер без сверки.
   */
  const view = inviteOptionsView(optionsQ);
  const allowedRoles = useMemo<Role[]>(
    () => bulkInviteRoles(inviteOptionsView({ isError: optionsQ.isError, data: optionsQ.data })),
    [optionsQ.isError, optionsQ.data],
  );
  const roleOrder = useMemo<Role[]>(() => {
    const v = inviteOptionsView({ isError: false, data: optionsQ.data });
    return v.state === 'ready' ? v.order : [];
  }, [optionsQ.data]);
  const noRights = view.state === 'ready' && allowedRoles.length === 0;
  const invitableNames = (optionsQ.data?.items ?? []).map((i) => i.entity_name ?? i.entity_id);
  const bulkEntityId = optionsQ.data?.bulk?.entity_id ?? null;
  const bulkCompanyName = bulkEntityId
    ? (optionsQ.data?.items.find((i) => i.entity_id === bulkEntityId)?.entity_name ?? null)
    : null;
  useEffect(() => {
    setDefaultRole((prev) => fitInviteRole(prev, allowedRoles, roleOrder));
  }, [allowedRoles, roleOrder]);

  // Парсинг — из вставленного текста ИЛИ из CSV-файла (оба пишут в `raw`).
  // Роль не обязательна построчно: берётся defaultRole, переопределяется `email, role`
  // или ручным выбором в превью (roleOverrides).
  const rows = useMemo(() => {
    const base = parseInvites(raw, defaultRole, allowedRoles);
    // С-I7 Р.5: роль строки, назначенная до ответа, сверяется с ответом — выше своей нельзя.
    return base.map((r): ParsedRow => {
      const ov = roleOverrides[r.email];
      if (!ov) return r;
      if ((r.valid || r.roleAbove) && !allowedRoles.includes(ov)) {
        return {
          ...r,
          role: ov,
          valid: false,
          reason: m.route_admin_users_bulk_reason_role_above(),
          roleAbove: true,
        };
      }
      // С-I7, круг 2 гейта (UX-2): строка «роль выше вашей», которой выбрали допустимую роль, уходит.
      if (r.roleAbove) return { email: r.email, role: ov, valid: true };
      return { ...r, role: ov };
    });
  }, [raw, defaultRole, roleOverrides, allowedRoles]);

  const handleFile = async (file: File): Promise<void> => {
    const text = await file.text();
    setRaw((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text));
    setResult(null);
  };

  const onDrop = (ev: React.DragEvent<HTMLDivElement>): void => {
    ev.preventDefault();
    setDragging(false);
    const f = ev.dataTransfer.files[0];
    if (f) void handleFile(f);
  };

  const toast = useToast();
  // F109: итог массового — статусы писем из списка приглашений (тот же кэш, перечитывается, пока есть «отправляется»).
  const invitesQ = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => fetchInvites(),
    enabled: result !== null,
    refetchInterval: invitesPollInterval,
  });
  const invitesByEmail = new Map(
    ((invitesQ.data?.items ?? []) as AdminInvite[])
      .filter((i) => i.status === 'pending')
      .map((i) => [i.email.toLowerCase(), i] as const),
  );
  const deliveryOf = (
    email: string,
    emailSent: boolean | null | undefined,
  ): AdminInvite['email_delivery'] =>
    invitesByEmail.get(email.toLowerCase())?.email_delivery ??
    (emailSent === false ? 'failed' : 'queued');
  const bulkInvites = (invitesQ.data?.items ?? []) as AdminInvite[];
  const bulkChannelDownSince = inviteChannelDownSince(bulkInvites, replacedInviteIds(bulkInvites));
  const deliveryCounts = { sent: 0, queued: 0, failed: 0, unknown: 0 };
  for (const c of result?.created ?? []) {
    const d = deliveryOf(c.email, c.email_sent);
    if (d) deliveryCounts[d] += 1;
  }
  // С-I3 Р.2 (TCA-F10): почта лежит — «отправляется: N» лгало; эти письма не уйдут сами.
  const deliverySummary = bulkDeliverySummary(
    deliveryCounts,
    result?.created.length ?? 0,
    bulkChannelDownSince !== null,
  );
  // Круг 2 (UX-5): не ушедшие письма — повтор прямо из итога, по одному на приглашение.
  const retryTargets = (result?.created ?? [])
    .map((c) => ({ email: c.email, invite: invitesByEmail.get(c.email.toLowerCase()) }))
    .filter(
      (t): t is { email: string; invite: AdminInvite } =>
        !!t.invite &&
        (t.invite.email_delivery === 'failed' || t.invite.email_delivery === 'unknown'),
    );
  const retryIds = retryTargets.map((t) => t.invite.id);
  // С-I3 Р.2, круг 1 (UX, MAJOR): сколько уже скопированных ссылок погасит выпуск новых при лежащей почте.
  const reissueKillsCopied = bulkChannelDownSince
    ? retryTargets.filter((t) => copied.has(t.email)).length
    : 0;
  const [confirmReissue, setConfirmReissue] = useState(false);
  const qcBulk = useQueryClient();
  const retryMut = useMutation({
    // Круг 4 гейта Р.8 (UX-1, MAJOR): исход — по каждой строке. Сбой k-й строки ронял весь цикл, и
    // строки 1…k-1 (их токены сервер уже заменил) оставались с прежними, нерабочими ссылками.
    mutationFn: async () => {
      const fresh: { email: string; link: string | null }[] = [];
      let failed = 0;
      for (const t of retryTargets) {
        try {
          const resp = await resendInvite(t.invite.id);
          fresh.push({ email: t.email, link: resp.accept_url ?? null });
        } catch {
          failed += 1;
        }
      }
      return { fresh, failed };
    },
    /*
     * С-I2 Р.8, круг 3 гейта (UX-1, MAJOR): повтор гасит прежние ссылки — строки получают новые из ответов,
     * отметки «Скопировано» и поля отказа по этим строкам сняты, строка состояния говорит, что прежние
     * не работают. Счётчики — этого запуска (круг 5, UX-2): прежний «выпустил» не склеивается с отказами.
     */
    onSuccess: ({ fresh, failed }) => {
      const byEmail = new Map(fresh.flatMap((f) => (f.link ? [[f.email, f.link] as const] : [])));
      setRetryFailedCount(failed);
      setReplacedLinks(byEmail.size);
      if (byEmail.size === 0) return;
      setResult(
        (prev) =>
          prev && {
            ...prev,
            created: prev.created.map((c) => {
              const link = byEmail.get(c.email);
              return link ? { ...c, accept_url: link } : c;
            }),
          },
      );
      const drop = (prev: ReadonlySet<string>): Set<string> =>
        new Set([...prev].filter((e) => !byEmail.has(e)));
      setCopied(drop);
      setCopyFailed(drop);
      setCopyAllFailed(false);
    },
    onSettled: () => {
      void qcBulk.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
  });
  const submitMut = useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: () =>
      bulkInviteUsers(rows.filter((r) => r.valid).map((r) => ({ email: r.email, role: r.role }))),
    onSuccess: (r) => {
      setResult(r);
      // F109: исход писем в ответе неизвестен (очередь) — счётчик по статусу живёт в итоге диалога.
      toast.push({
        variant: r.failed.length > 0 ? 'warning' : 'info',
        title: m.admin_invite_toast_bulk_title(),
        description: m.admin_invite_toast_bulk_body({
          created: r.created.length,
          failed: r.failed.length,
        }),
        durationMs: 8000,
      });
      onDone();
    },
    // С-I7 Р.5 (ACC-F10): отказ — строкой в окне; тост ложился под подложку модального окна. Строка
    // относится к отправленному списку: правка списка или ролей её гасит (сверка по снимку).
    onError: (err) =>
      setSubmitError({ text: inviteErrorText(err), raw, defaultRole, overrides: roleOverrides }),
  });
  const [submitError, setSubmitError] = useState<{
    text: string;
    raw: string;
    defaultRole: Role;
    overrides: Record<string, Role>;
  } | null>(null);
  const submitErrorText =
    submitError &&
    submitError.raw === raw &&
    submitError.defaultRole === defaultRole &&
    submitError.overrides === roleOverrides
      ? submitError.text
      : null;

  const doClose = (): void => {
    setRaw('');
    setRoleOverrides({});
    setSubmitError(null);
    setResult(null);
    setCopied(new Set());
    setConfirmClose(false);
    setCopyFailed(new Set());
    setCopyAllFailed(false);
    setReplacedLinks(0);
    setRetryFailedCount(0);
    onClose();
  };
  const linkRows = (result?.created ?? []).filter((c) => c.accept_url);
  const uncopiedLinks = linkRows.filter((c) => !copied.has(c.email)).length;
  const close = (): void => {
    if (uncopiedLinks > 0) setConfirmClose(true);
    else doClose();
  };
  const markCopied = (emails: readonly string[]): void =>
    setCopied((prev) => new Set([...prev, ...emails]));
  const allPairs = linkRows.map((c) => `${c.email} — ${c.accept_url}`).join('\n');
  const copyAllLinks = (): Promise<boolean> =>
    navigator.clipboard.writeText(allPairs).then(
      () => {
        markCopied(linkRows.map((c) => c.email));
        toast.push({ variant: 'success', title: m.admin_invite_links_copied_all() });
        return true;
      },
      () => {
        setCopyAllFailed(true);
        return false;
      },
    );
  const copyRowLink = (email: string, link: string): void => {
    void navigator.clipboard.writeText(link).then(
      () => {
        markCopied([email]);
        toast.push({
          variant: 'success',
          title: m.admin_invite_link_copied(),
          description: m.admin_invite_link_copied_body(),
        });
      },
      () => {
        setCopyFailed((prev) => new Set([...prev, email]));
        setFocusLinkField(email);
      },
    );
  };
  // Круг 2 (UX-4): выход, решающий задачу в один шаг, — скопировать все и закрыть.
  const copyAllAndClose = (): void => {
    void copyAllLinks().then((ok) => {
      if (ok) doClose();
      else setConfirmClose(false);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={m.route_admin_users_bulk_title()}
      size="lg"
      footer={
        <>
          {/* С-I3 Р.2, круг 1 (UX, MINOR): все ссылки скопированы — закрыть и есть следующий шаг. */}
          <Button
            variant={
              result && linkRows.length > 0 && uncopiedLinks === 0
                ? 'primary'
                : // Круг 2 гейта С-I6 (UI): действия нет — единственная кнопка Secondary, не Ghost.
                  !result && (noRights || view.state !== 'ready')
                  ? 'secondary'
                  : 'ghost'
            }
            onClick={close}
          >
            {m.common_button_close()}
          </Button>
          {!result && view.state === 'ready' && rows.length > 0 && (
            <Button
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending || noRights || rows.every((r) => !r.valid)}
            >
              {submitMut.isPending
                ? m.route_admin_users_bulk_sending()
                : m.route_admin_users_bulk_submit({
                    count: rows.filter((r) => r.valid).length,
                  })}
            </Button>
          )}
        </>
      }
    >
      {/* С-I6 Р.2: в компании запроса приглашать нельзя — только пояснение; поле адресов и CSV не
          предлагаются (отправить их всё равно было бы нельзя). */}
      {/* Круг 2 гейта С-I6 (UX, MAJOR): совет «переключитесь» — только если есть куда, с названиями
          компаний; иначе — тот же текст, что в одиночном окне (некуда и к кому идти). */}
      {!result && view.state !== 'ready' && (
        <InviteOptionsStatus
          state={view.state}
          error={optionsQ.error}
          onRetry={() => void optionsQ.refetch()}
        />
      )}
      {!result && noRights && (
        <p className={[styles.inviteRightsNote, styles.inviteRightsNoteFlush].join(' ')}>
          <Info size={14} aria-hidden="true" />
          {invitableNames.length > 0
            ? m.route_admin_users_bulk_no_rights({ companies: invitableNames.join(', ') })
            : m.route_admin_users_invite_no_companies()}
        </p>
      )}
      {!result && view.state === 'ready' && !noRights && (
        <>
          {/* С-I7, круг 1 гейта (UX-4): окно называет компанию, в которую идут приглашения и от которой
              считается «роль выше вашей». */}
          {bulkCompanyName && (
            <p className={[styles.inviteRightsNote, styles.inviteRightsNoteFlush].join(' ')}>
              <Info size={14} aria-hidden="true" />
              {m.route_admin_users_bulk_target_company({ company: bulkCompanyName })}
            </p>
          )}
          <div
            className={styles.sectionGap}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <Select
              label={m.route_admin_users_bulk_role_for_all()}
              value={defaultRole}
              onChange={(e) => setDefaultRole(e.target.value as Role)}
              options={allowedRoles.map((r) => ({ value: r, label: getRoleLabel(r) }))}
            />
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={6}
              aria-label={m.route_admin_users_bulk_paste_aria()}
              placeholder={m.route_admin_users_bulk_paste_placeholder()}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)',
                padding: 8,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm, 6px)',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                resize: 'vertical',
                width: '100%',
              }}
            />
          </div>
          <div
            className={[styles.dropZone, dragging ? styles.dropZoneActive : ''].join(' ')}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={m.route_admin_users_bulk_dropzone_aria()}
          >
            {m.route_admin_users_bulk_dropzone_hint()}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className={styles.hiddenInput}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          {rows.length > 0 && (
            <div className={styles.sectionGap}>
              <Table
                ariaLabel={m.route_admin_users_bulk_preview_aria()}
                data={rows}
                rowKey={(r, i) => `${r.email}-${i}`}
                columns={[
                  {
                    id: 'email',
                    header: m.route_admin_users_col_email(),
                    // С-I7 Р.5: адрес не переносится по дефису — тот же перенос, что в итоге.
                    accessor: (r) => <EmailWrap text={r.email} />,
                  },
                  {
                    id: 'role',
                    header: m.route_admin_users_bulk_col_role(),
                    // С-I7, круг 1 гейта (UI-1): самая длинная роль («Руководитель производства»)
                    // читается в поле целиком; было 180 px — поле резало её многоточием.
                    width: '250px',
                    // Роль редактируется прямо в строке превью (для невалидных адресов
                    // выбор отключён — они всё равно не отправляются).
                    accessor: (r) => (
                      <div style={{ minWidth: 150 }}>
                        <Select
                          label=""
                          size="sm"
                          value={r.role}
                          disabled={!r.valid && !r.roleAbove}
                          onChange={(e) =>
                            setRoleOverrides((prev) => ({
                              ...prev,
                              [r.email]: e.target.value as Role,
                            }))
                          }
                          // С-I7 Р.5: роль строки вне разрешённых (выше своей) — подписью в
                          // выключенном выборе, а не «—»: видно, что именно не подошло.
                          options={(allowedRoles.includes(r.role)
                            ? allowedRoles
                            : [r.role, ...allowedRoles]
                          ).map((rr) => ({
                            value: rr,
                            label: getRoleLabel(rr),
                          }))}
                        />
                      </div>
                    ),
                  },
                  {
                    id: 'valid',
                    header: m.route_admin_users_bulk_col_check(),
                    accessor: (r) =>
                      r.valid ? (
                        <span className={styles.validBadge}>
                          {m.route_admin_users_bulk_check_ok()}
                        </span>
                      ) : (
                        <span className={styles.invalidBadge}>
                          {r.reason ?? m.route_admin_users_bulk_reason_invalid()}
                          {/* С-I7 Р.5: глобального `sr-only` в проекте нет — «ошибка валидации»
                              печаталась видимым хвостом причины; класс — модульный. */}
                          <span className={shared.srOnly}>
                            {' '}
                            {m.route_admin_users_bulk_check_invalid()}
                          </span>
                        </span>
                      ),
                  },
                ]}
              />
            </div>
          )}
        </>
      )}
      {!result && submitErrorText && (
        <p className={styles.inviteSubmitError} role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>
            <strong>{m.admin_invite_toast_error_title()}.</strong> {submitErrorText}
          </span>
        </p>
      )}
      {result && (
        <div>
          <h3 className={shared.sectionTitle}>
            {m.route_admin_users_bulk_result_created({ count: result.created.length })}
          </h3>
          {/* F109 (TCA-F10): статус каждого письма — по списку приглашений после обновления. */}
          <p className={styles.bulkDeliverySummary} data-tone={deliverySummary.tone ?? undefined}>
            {/* С-I3 Р.2, круг 1 (UI): тон — у значка, текст — основным грейдом (§ 4.1b, «сводка»). */}
            {deliverySummary.tone === 'warn' && (
              <MailWarning size={14} aria-hidden="true" className={styles.bulkDeliveryWarnIcon} />
            )}
            {deliverySummary.text}
          </p>
          {bulkChannelDownSince && <InviteChannelDown since={bulkChannelDownSince} inDialog />}
          {retryIds.length > 0 && (
            <Button
              size="sm"
              variant={bulkChannelDownSince ? 'ghost' : 'secondary'}
              // Круг 2 гейта (UI, MINOR): ghost встаёт по левому краю колонки, как записки над и под ним.
              className={bulkChannelDownSince ? styles.bulkReissueFlush : undefined}
              leadingIcon={bulkChannelDownSince ? <RefreshCw size={12} /> : <Mail size={12} />}
              loading={retryMut.isPending}
              onClick={() => (reissueKillsCopied > 0 ? setConfirmReissue(true) : retryMut.mutate())}
            >
              {/* Круг 4 (UX-2): при лежащей почте повтор ничего не доставит — он только выпускает новые
                  ссылки; так и названо. */}
              {bulkChannelDownSince
                ? m.admin_invite_bulk_reissue_links({ count: retryIds.length })
                : m.route_admin_users_bulk_retry_failed({ count: retryIds.length })}
            </Button>
          )}
          {(replacedLinks > 0 || retryFailedCount > 0) && (
            <p role="status" className={styles.bulkLinkReplaced}>
              {/* Круг 4 (UI-3): новость о замене — со значком, не ещё одна серая подсказка. С-I3 Р.2,
                  круг 1 (UI): над «Скопировать все» — кнопка остаётся рядом со столбцом кнопок строк. */}
              <RefreshCw size={14} aria-hidden="true" className={styles.bulkLinkReplacedIcon} />
              {[
                replacedLinks > 0
                  ? m.admin_invite_bulk_links_replaced_body({ count: replacedLinks })
                  : '',
                retryFailedCount > 0
                  ? m.admin_invite_bulk_retry_failed_some({ count: retryFailedCount })
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            </p>
          )}
          {linkRows.length > 0 && (
            <div className={styles.bulkLinkBar}>
              {/* Круг 3 (UX-2): почта лежит — та же формула, что в тосте: «передайте сами». */}
              <p className={styles.bulkLinkNote}>
                {bulkChannelDownSince
                  ? m.admin_invite_bulk_links_mail_down()
                  : m.admin_invite_bulk_links_once()}
              </p>
              <Button
                ref={copyAllRef}
                size="sm"
                // С-I3 Р.2: пока есть нескопированные ссылки — главное действие окна (Primary).
                variant={copyAllVariant(uncopiedLinks)}
                className={styles.bulkLinkAll}
                leadingIcon={<Copy size={12} />}
                onClick={() => void copyAllLinks()}
              >
                {m.admin_invite_copy_all_links()}
              </Button>
            </div>
          )}
          {copyAllFailed && (
            <div className={styles.bulkLinkFallback}>
              <p className={styles.bulkLinkNote}>{m.admin_invite_links_copy_failed_body()}</p>
              <textarea
                readOnly
                className={styles.bulkLinkField}
                // пара «адрес — ссылка» — одна строка поля, без переноса; лишняя строка — под горизонтальную
                // полосу прокрутки (круг 3, UI-2), вертикальная — только сверх шести пар
                wrap="off"
                rows={Math.min(linkRows.length, 6) + 1}
                data-scroll={linkRows.length > 6 ? 'y' : undefined}
                value={allPairs}
                aria-label={m.admin_invite_links_field_label()}
                onFocus={(e) => e.currentTarget.select()}
                // Круг 3 (UX-3): скопированы только те строки, чья ссылка целиком в выделении.
                onCopy={(e) => {
                  const picked = selectedText(e.currentTarget);
                  markCopied(
                    linkRows
                      .filter((c) => c.accept_url && picked.includes(c.accept_url))
                      .map((c) => c.email),
                  );
                }}
              />
            </div>
          )}
          {/* Круг 4 (UI-2): без поля пар подпись поля строки видима; с ним — только для чтеца. */}
          {copyFailed.size > 0 && (
            <p
              id="bulk-link-copy-failed"
              className={copyAllFailed ? shared.srOnly : styles.bulkLinkFailedNote}
            >
              {m.admin_invite_link_copy_failed_body()}
            </p>
          )}
          <ul className={styles.bulkDeliveryList}>
            {result.created.map((c) => (
              <li key={c.id ?? c.user_id ?? c.email}>
                <span className={styles.inviteEmail}>
                  <EmailWrap text={c.email} />
                </span>
                <InviteDeliveryStatus
                  delivery={deliveryOf(c.email, c.email_sent)}
                  at={invitesByEmail.get(c.email.toLowerCase())?.email_status_at}
                  channelDown={bulkChannelDownSince !== null}
                />
                {/* С-I2 Р.8 (В-50): ссылка этой строки — один раз, только в итоге массового. */}
                {c.accept_url ? (
                  <Button
                    size="sm"
                    // Круг 2 (UI-5): скопированная строка тише — оставшиеся находятся взглядом.
                    variant={copied.has(c.email) ? 'ghost' : 'secondary'}
                    aria-label={`${
                      copied.has(c.email)
                        ? m.admin_invite_link_copied_short()
                        : m.admin_invite_toast_copy_link()
                    }: ${c.email}`}
                    leadingIcon={
                      copied.has(c.email) ? (
                        <Check size={12} className={styles.copiedIcon} />
                      ) : (
                        <Copy size={12} />
                      )
                    }
                    onClick={() => copyRowLink(c.email, c.accept_url as string)}
                  >
                    {copied.has(c.email)
                      ? m.admin_invite_link_copied_short()
                      : m.admin_invite_toast_copy_link()}
                  </Button>
                ) : (
                  <span />
                )}
                {/* Круг 3 (UI-3): поле не прячется после копирования — буфер отказывает снова. */}
                {c.accept_url && copyFailed.has(c.email) && (
                  <input
                    type="text"
                    readOnly
                    className={styles.bulkLinkField}
                    value={c.accept_url}
                    aria-label={`${m.admin_invite_link_field_label()}: ${c.email}`}
                    aria-describedby="bulk-link-copy-failed"
                    data-link-field={c.email}
                    onFocus={(e) => e.currentTarget.select()}
                    onCopy={(e) => {
                      if (selectedText(e.currentTarget).includes(c.accept_url as string))
                        markCopied([c.email]);
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
          {result.failed.length > 0 && (
            <>
              <h3 className={shared.sectionTitle}>
                {m.route_admin_users_bulk_result_failed({ count: result.failed.length })}
              </h3>
              <ul>
                {result.failed.map((f) => (
                  <li key={f.email}>
                    {/* С-I6 Р.2 — причина отказа строки текстом по коду, а не сырым кодом. */}
                    <span className={styles.mono}>{f.email}</span> — {bulkFailedReasonText(f)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {/*
       * С-I2 Р.8, круг 1 (UX, MAJOR): нескопированные ссылки после закрытия не получить.
       * Круг 2: заголовок без числа (UI-4 — «окно?» висело одно), основное действие — «Скопировать все
       * и закрыть» (UX-4), разрушающее «Закрыть без ссылок» — тихое, фокус — на основном.
       */}
      {/*
       * С-I3 Р.2, круг 1 (UX, MAJOR): при лежащей почте выпуск новых ссылок письма не доставит, а уже
       * скопированные (и, может, переданные) ссылки погасит — только с подтверждением.
       */}
      <ConfirmDialog
        open={confirmReissue}
        title={m.admin_invite_bulk_reissue_confirm_title()}
        description={m.admin_invite_bulk_reissue_confirm_body({ count: reissueKillsCopied })}
        confirmLabel={m.admin_invite_bulk_reissue_confirm_ok()}
        variant="danger"
        onCancel={() => setConfirmReissue(false)}
        onConfirm={() => {
          setConfirmReissue(false);
          retryMut.mutate();
        }}
      />
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={m.admin_invite_bulk_close_title()}
        // два действия в одну строку — в `sm` основное уходило вторым рядом; «вернуться» — крестик и Esc
        size="md"
        describedBy="bulk-close-desc"
        footer={
          <>
            <Button variant="ghost" onClick={doClose}>
              {m.admin_invite_bulk_close_confirm()}
            </Button>
            <Button
              variant="primary"
              leadingIcon={<Copy size={14} />}
              ref={copyAllCloseRef}
              onClick={copyAllAndClose}
            >
              {m.admin_invite_copy_all_and_close()}
            </Button>
          </>
        }
      >
        <p id="bulk-close-desc" className={styles.bulkCloseBody}>
          {m.admin_invite_bulk_close_body({ count: uncopiedLinks })}
        </p>
      </Dialog>
    </Dialog>
  );
}

/**
 * Парсинг списка приглашений. Принимает:
 *  - простой список адресов (по строке / через запятую / пробел / точку с запятой);
 *  - CSV `email,role` (роль построчно переопределяет defaultRole);
 *  - заголовок `email,role` пропускается.
 * Роль по умолчанию (defaultRole) применяется ко всем строкам без явной роли.
 * С-I6 Р.2 (ACC-B22): роль вне `allowedRoles` (выше своей в компании) — строка невалидна с причиной.
 */
function parseInvites(text: string, defaultRole: Role, allowedRoles: readonly Role[]): ParsedRow[] {
  const rawRows = text
    .split(/[\r\n;]+/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (rawRows.length === 0) return [];
  // Пропуск CSV-заголовка `email,role` (если это не адрес).
  const first = rawRows[0]!;
  const body =
    /email/i.test(first) && /role/i.test(first) && !first.includes('@')
      ? rawRows.slice(1)
      : rawRows;

  const out: ParsedRow[] = [];
  const seen = new Set<string>();
  const addRow = (emailRaw: string, role: Role): void => {
    const email = emailRaw.trim().toLowerCase();
    if (!email) return;
    let valid = true;
    let reason: string | undefined;
    let roleAbove = false;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      valid = false;
      reason = m.route_admin_users_bulk_reason_invalid_email();
    } else if (!ENTITY_MEMBERSHIP_ROLES.includes(role)) {
      valid = false;
      reason = m.route_admin_users_bulk_reason_invalid_role();
    } else if (!allowedRoles.includes(role)) {
      valid = false;
      reason = m.route_admin_users_bulk_reason_role_above();
      roleAbove = true;
    } else if (seen.has(email)) {
      valid = false;
      reason = m.route_admin_users_bulk_reason_duplicate();
    }
    seen.add(email);
    out.push({
      email,
      role,
      valid,
      ...(reason ? { reason } : {}),
      ...(roleAbove ? { roleAbove } : {}),
    });
  };

  for (const line of body) {
    // С запятой — CSV-токены; без запятой — список через пробел.
    const tokens = (line.includes(',') ? line.split(',') : line.split(/\s+/))
      .map((s) => s.trim())
      .filter(Boolean);
    if (tokens.length === 0) continue;
    const last = tokens[tokens.length - 1]!;
    if (tokens.length >= 2 && ENTITY_MEMBERSHIP_ROLES.includes(last as Role)) {
      // Явная роль в конце строки: применяем ко всем адресам строки.
      const role = last as Role;
      for (const email of tokens.slice(0, -1)) addRow(email, role);
    } else {
      // Ролей нет — все токены это адреса, роль = defaultRole.
      for (const email of tokens) addRow(email, defaultRole);
    }
  }
  return out;
}

/**
 * F109 — письма не уходят вообще (`inviteChannelDownSince`): один сигнал в разделе приглашений и в итоге
 * массового (круг 4, UX MAJOR: окно советовало повтор, когда раздел говорил «не поможет»).
 */
function InviteChannelDown({
  since,
  inDialog = false,
}: {
  since: string;
  /** В окне итога массового — без плёнки в тёмной теме (С-I3 Р.2, круг 1 гейта, UI MAJOR). */
  inDialog?: boolean;
}): JSX.Element {
  const locale = useLocale();
  return (
    <div
      role="status"
      className={styles.inviteChannelDown}
      data-in-dialog={inDialog ? '' : undefined}
    >
      <AlertTriangle size={16} aria-hidden="true" />
      <span>
        {m.invite_channel_down({
          time: new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(since)),
        })}
        {/* С-I3 Р.2, круг 2 гейта (UI и UX, MAJOR): в списке ссылки нет — одна фраза раздела вместо
            подсказки в каждой строке: письмо повтором не уйдёт, ссылку выпускает кнопка строки. */}
        {!inDialog && ` ${m.invite_channel_down_list_hint()}`}
      </span>
    </div>
  );
}

/** F109, круг 3 (UX-5): тост ведёт к разделу приглашений — он ниже реестра пользователей. */
/**
 * С-I2 Р.8 (TCA-B10, В-50) — скопировать ссылку приглашения. Ссылка приходит один раз (ответ выпуска),
 * сервер её не хранит: потерянную — только выпустить заново («Отправить снова»).
 */
type PushToast = ReturnType<typeof useToast>['push'];

/**
 * Круг 3 гейта Р.8 (UI-1): адрес в тексте тоста не рвётся по дефису («si2-» / «bulk2-b@…») —
 * неразрывный дефис U+2011, как у `EmailWrap` в таблицах.
 */
function noBreakHyphen(email: string): string {
  return email.replace(/-/g, '\u2011');
}

/** Выделенная часть поля — что именно копирует человек из поля отказа буфера. */
function selectedText(el: HTMLInputElement | HTMLTextAreaElement): string {
  return el.value.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0);
}

function copyInviteLink(link: string, push: PushToast, onCopied?: () => void): Promise<boolean> {
  return navigator.clipboard.writeText(link).then(
    () => {
      onCopied?.();
      push({
        variant: 'success',
        title: m.admin_invite_link_copied(),
        description: m.admin_invite_link_copied_body(),
      });
      return true;
    },
    () => {
      // Круг 1 гейта Р.8 (UI-2): буфер отказал — ссылка остаётся видимой полем, выделить вручную.
      push({
        variant: 'error',
        title: m.common_toast_copy_failed(),
        description: m.admin_invite_link_copy_failed_body(),
        field: { value: link, label: m.admin_invite_link_field_label() },
        durationMs: 0,
      });
      return false;
    },
  );
}

/**
 * Круг 1 гейта Р.8 (UX-1/2/5, UI-1/2/4): одноразовая ссылка не живёт в тосте с таймером — тост висит,
 * пока его не закроют или не скопируют ссылку; ссылка видна полем; копирование — основное действие со
 * значком, «К приглашениям» — тихое и называет, куда ведёт.
 */
function inviteLinkToastFields(
  link: string,
  push: PushToast,
): Pick<ToastItem, 'durationMs' | 'action' | 'secondaryAction' | 'field'> {
  return {
    durationMs: 0,
    field: { value: link, label: m.admin_invite_link_field_label() },
    action: {
      label: m.admin_invite_toast_copy_link(),
      icon: <Copy size={12} aria-hidden="true" />,
      onClick: () => copyInviteLink(link, push),
    },
    // Круг 2 (UX-1, MAJOR): переход только прокручивает — тост со ссылкой остаётся.
    secondaryAction: {
      label: m.admin_invite_toast_to_invites(),
      onClick: scrollToInvites,
      keepOpen: true,
    },
  };
}

/** Круг 2 гейта Р.8 (UX-3): почта лежит — по тому же признаку, что баннер раздела приглашений. */
function inviteMailDown(qc: ReturnType<typeof useQueryClient>): boolean {
  const items = qc.getQueryData<{ items?: AdminInvite[] }>(['admin', 'invites'])?.items ?? [];
  return inviteChannelDownSince(items, replacedInviteIds(items)) !== null;
}

function scrollToInvites(): void {
  document.getElementById('admin-invites')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

/** F109: пока есть письмо «отправляется», список приглашений перечитывается раз в 5 с. */
function invitesPollInterval(q: { state: { data?: { items?: AdminInvite[] } | undefined } }):
  | number
  | false {
  return (q.state.data?.items ?? []).some((i) => i.email_delivery === 'queued') ? 5000 : false;
}

// ─── Приглашения (статус + actions) ──────────────────────────────────────
function InvitesSection({
  filterActive,
  canRevoke,
}: {
  filterActive: boolean;
  /** С-I8 Р.5: «Отозвать» — от `admin` (`ACC-B41` (2)), руководителю компании кнопки нет. */
  canRevoke: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const locale = useLocale();
  /*
   * F109 (С-I): колонка «Письмо» не помещалась — даты короче («6 окт., 11:28», полная — в подсказке),
   * как в колонке «Активность» реестра; секунды и год в строке не нужны.
   */
  const shortStr = (iso: string): string =>
    new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  const shortDay = (iso: string): string =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(iso));
  const shortTs = (iso: string): JSX.Element => (
    <span title={notifTimeTitle(iso, locale)}>{shortStr(iso)}</span>
  );
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => fetchInvites(),
    // F109 (TCA-F10): пока хоть одно письмо «отправляется» — список перечитывается, и итог виден без F5.
    refetchInterval: invitesPollInterval,
  });

  const items = (data?.items ?? []) as AdminInvite[];
  // F109, круг 2 (UX-1): строки, погашенные повтором, — «Заменено повтором» без действий.
  const replacedIds = useMemo(() => replacedInviteIds(items), [items]);
  // Круг 3 (UX-1, MAJOR): письма не уходят вообще — сигнал на раздел, а не «отправьте снова» в каждой строке.
  const channelDownSince = useMemo(
    () => inviteChannelDownSince(items, replacedIds),
    [items, replacedIds],
  );

  // С-I3 Р.2, круг 3 гейта (UX, MAJOR): выпуск заново гасит ссылку, которую уже могли передать.
  const [reissueTarget, setReissueTarget] = useState<AdminInvite | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AdminInvite | null>(null);
  // С-I3 Р.2: почта лежит, письмо этой строки не ушло — путь к ссылке только через выпуск новой.
  const stuckRow = (r: AdminInvite): boolean =>
    channelDownSince !== null &&
    r.status === 'pending' &&
    r.email_delivery !== null &&
    r.email_delivery !== undefined &&
    r.email_delivery !== 'sent' &&
    // «Статус не отслеживался» (до учёта статуса) — не застрявшее письмо, кнопка прежняя.
    !(r.email_delivery === 'unknown' && !r.email_status_at);

  const resendMut = useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: (resp, id) => {
      const target = items.find((i) => i.id === id)?.email ?? '';
      void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
      const sent = resp.email_sent !== false;
      // Круг 2 гейта Р.8 (UX-2, UX-3): почта лежит / письмо не ушло — «передайте ссылку сами».
      const mailDown = sent && Boolean(resp.accept_url) && channelDownSince !== null;
      toast.push({
        variant: sent && !mailDown ? 'info' : 'warning',
        title: mailDown
          ? m.admin_invite_toast_mail_down_title()
          : sent
            ? m.admin_invite_toast_resent_title()
            : m.admin_invite_toast_email_failed_title(),
        description: mailDown
          ? m.admin_invite_toast_mail_down_body({ email: noBreakHyphen(target) })
          : sent
            ? resp.accept_url
              ? m.admin_invite_toast_resent_body_link({ email: noBreakHyphen(target) })
              : m.admin_invite_toast_resent_body({ email: noBreakHyphen(target) })
            : resp.accept_url
              ? m.admin_invite_toast_email_failed_body_link({ email: noBreakHyphen(target) })
              : m.admin_invite_toast_email_failed_body({ email: noBreakHyphen(target) }),
        ...(resp.email_sent !== false ? {} : { durationMs: 10000 }),
        // С-I2 Р.8 (В-50): новая ссылка — один раз, копируется из тоста.
        ...(resp.accept_url
          ? inviteLinkToastFields(resp.accept_url, toast.push)
          : { action: { label: m.admin_invite_toast_show(), onClick: scrollToInvites } }),
      });
    },
    onError: (err) => {
      toast.push({
        variant: 'error',
        title: m.admin_invite_toast_error_title(),
        // С-I6 Р.2 — повтор чужого приглашения: отказ текстом по коду (`INSUFFICIENT_ROLE`).
        description: inviteErrorText(err),
      });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeInvite(id),
    onSuccess: (_resp, id) => {
      const target = items.find((i) => i.id === id)?.email ?? '';
      void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
      toast.push({
        variant: 'success',
        title: m.admin_invite_toast_revoked_title(),
        description: m.admin_invite_toast_revoked_body({ email: noBreakHyphen(target) }),
      });
    },
    onError: (err) => {
      toast.push({
        variant: 'error',
        title: m.admin_invite_toast_error_title(),
        description: m.admin_invite_toast_error_body({ message: (err as Error).message }),
      });
    },
  });

  return (
    <div className={styles.sectionGap}>
      <h2 id="admin-invites" className={shared.sectionTitle}>
        {m.admin_invites_section_title()}
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-14)', margin: 0 }}>
        {m.admin_invites_section_description()}
        {filterActive ? ` ${m.route_admin_users_filter_invites_note()}` : ''}
      </p>
      {channelDownSince && <InviteChannelDown since={channelDownSince} />}
      <ConfirmDialog
        open={revokeTarget !== null}
        title={m.admin_invites_revoke_confirm_title()}
        description={m.admin_invites_revoke_confirm({
          email: noBreakHyphen(revokeTarget?.email ?? ''),
        })}
        confirmLabel={m.admin_invites_btn_revoke()}
        variant="danger"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revokeTarget) revokeMut.mutate(revokeTarget.id);
          setRevokeTarget(null);
        }}
      />
      <ConfirmDialog
        open={reissueTarget !== null}
        title={m.admin_invites_reissue_confirm_title()}
        description={m.admin_invites_reissue_confirm_body({
          email: noBreakHyphen(reissueTarget?.email ?? ''),
        })}
        confirmLabel={m.admin_invites_btn_issue_link()}
        variant="danger"
        onCancel={() => setReissueTarget(null)}
        onConfirm={() => {
          if (reissueTarget) resendMut.mutate(reissueTarget.id);
          setReissueTarget(null);
        }}
      />
      <Table
        ariaLabel={m.admin_invites_section_title()}
        loading={isLoading}
        // С-I3 Р.2, круг 2 (UI, MAJOR): колонка действий не уходит под горизонтальную прокрутку —
        // «Выпустить ссылку» единственный путь к ссылке при лежащей почте.
        stickyLastColumn
        data={items}
        rowKey={(r) => r.id}
        emptyTitle={m.admin_invites_empty()}
        columns={[
          {
            id: 'email',
            header: m.admin_invites_col_email(),
            // F109: адрес переносится только перед «@» (как в реестре, F106).
            // Круг 2 (UI-4): почта — шрифтом интерфейса, как в реестре над таблицей (моноширинный рвал строку).
            // Длинный адрес при нехватке места переносится и внутри — иначе он выдавливал колонку действий за край.
            // С-I3 Р.2, круг 3 гейта (UI MAJOR, UX MINOR): «Пригласил» — второй строкой под адресом, а
            // не своей колонкой: таблица не помещалась, и закреплённая колонка действий закрывала срок.
            accessor: (r) => (
              <span className={styles.inviteEmailStack}>
                <span className={styles.inviteEmailCell}>
                  <EmailWrap text={r.email} soft />
                </span>
                {r.invited_by_email && (
                  // Круг 4 гейта (UI, MAJOR): одной строкой с многоточием, полный адрес — в подсказке.
                  <span
                    className={styles.inviteInvitedBy}
                    title={m.admin_invites_invited_by_line({ email: r.invited_by_email })}
                  >
                    {m.admin_invites_invited_by_line({ email: noBreakHyphen(r.invited_by_email) })}
                  </span>
                )}
              </span>
            ),
          },
          {
            id: 'role',
            header: m.admin_invites_col_companies(),
            accessor: (r) =>
              r.memberships && r.memberships.length > 0 ? (
                <div className={styles.inviteMemberships}>
                  {/* Круг 4 гейта (UI, MINOR): роль — второй строкой подписи, как «пригласил» у адреса. */}
                  {r.memberships.map((mb) => (
                    <span key={mb.entity_id} className={styles.inviteMembership}>
                      <span>{mb.entity_name ?? mb.entity_id}</span>
                      <span className={styles.inviteMembershipRole}>{getRoleLabel(mb.role)}</span>
                    </span>
                  ))}
                </div>
              ) : (
                // holding-инвайт или legacy-запись без memberships — одна роль.
                getRoleLabel(r.role)
              ),
          },
          {
            id: 'status',
            header: m.admin_invites_col_status(),
            width: '1%',
            nowrap: true,
            accessor: (r) => (
              <InviteStatusBadge status={replacedIds.has(r.id) ? 'replaced' : r.status} />
            ),
          },
          {
            id: 'email_delivery',
            header: m.admin_invites_col_email_delivery(),
            // Круг 2 (UI-4): без статуса письма — `null`, таблица ставит своё «—».
            accessor: (r) =>
              r.email_delivery ? (
                <InviteDeliveryStatus
                  delivery={r.email_delivery}
                  at={r.email_status_at}
                  channelDown={channelDownSince !== null}
                  wrap
                />
              ) : null,
          },
          {
            /*
             * F109, круг 3: «Создано» — второй строкой срока, а не отдельной колонкой: колонка «Письмо» не
             * помещалась на 1284 (прокрутка 147 px). У заменённой строки срок — момент замены, так и подписан.
             */
            id: 'expires_at',
            header: m.admin_invites_col_expires_at(),
            width: '1%',
            nowrap: true,
            accessor: (r) => (
              <span className={styles.inviteDates}>
                {replacedIds.has(r.id) ? (
                  <span title={notifTimeTitle(r.expires_at, locale)}>
                    {m.admin_invites_replaced_at({ time: shortDay(r.expires_at) })}
                  </span>
                ) : (
                  shortTs(r.expires_at)
                )}
                <span className={styles.inviteCreated} title={notifTimeTitle(r.invited_at, locale)}>
                  {m.admin_invites_created_at({ time: shortDay(r.invited_at) })}
                </span>
              </span>
            ),
          },
          {
            id: 'actions',
            header: '',
            width: '1%',
            nowrap: true,
            /*
             * Круг 2 (UI-2, MAJOR): у принятого и заменённого — пустой узел (не «—» у левого края).
             * С-I3 Р.2, круг 4–5 гейта (UI): порядок в DOM совпадает с видимым и с порядком фокуса —
             * действие письма/ссылки первым, «Отозвать» за ним; на узком окне пара переносится, и
             * «Отозвать» уходит под кнопку (крестик над кнопкой с рамкой читался как «закрыть её»).
             */
            accessor: (r) =>
              r.status === 'accepted' || replacedIds.has(r.id) ? (
                <span />
              ) : (
                <div className={styles.inviteActions}>
                  {/* F109: письмо не ушло или застряло — «Отправить снова» заметнее (Secondary). */}
                  <Button
                    size="sm"
                    variant={
                      channelDownSince === null &&
                      (r.email_delivery === 'failed' ||
                        (r.email_delivery === 'unknown' && r.email_status_at))
                        ? 'secondary'
                        : 'ghost'
                    }
                    title={m.admin_invites_resend_title()}
                    leadingIcon={stuckRow(r) ? <Link2 size={12} /> : <Mail size={12} />}
                    onClick={() => (stuckRow(r) ? setReissueTarget(r) : resendMut.mutate(r.id))}
                    disabled={resendMut.isPending}
                  >
                    {/* С-I3 Р.2, круг 2 (UX, MAJOR): при лежащей почте кнопка не обещает отправку.
                        Круг 3 (UX, MAJOR): ссылку строки уже выдали при приглашении — это выпуск
                        заново (ghost, с подтверждением: прежняя перестанет работать). */}
                    {stuckRow(r) ? m.admin_invites_btn_issue_link() : m.admin_invites_btn_resend()}
                  </Button>
                  {r.status === 'pending' && canRevoke && (
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<XIcon size={12} />}
                      // Круг 4 гейта (UI, MAJOR): подтверждение отзыва — диалогом STAITER, как у выпуска
                      // заново; нативное окно ставило фокус на «ОК», и Enter сразу отзывал.
                      onClick={() => setRevokeTarget(r)}
                      disabled={revokeMut.isPending}
                    >
                      {m.admin_invites_btn_revoke()}
                    </Button>
                  )}
                </div>
              ),
          },
        ]}
      />
    </div>
  );
}

/**
 * F109, круг 3 (UI-3, MAJOR): статус приглашения — общий `StatusBadge`, а не инлайн-стили. «В ожидании» —
 * обычное состояние, нейтрально (§ 1.3); янтарь — у истёкшего (нужно действие), «заменено» — терминальное
 * нейтральное, «принято» — завершено.
 */
function InviteStatusBadge({
  status,
}: {
  status: AdminInvite['status'] | 'replaced';
}): JSX.Element {
  if (status === 'pending') {
    return <StatusBadge status="neutral" size="sm" label={m.admin_invites_status_pending()} />;
  }
  if (status === 'accepted') {
    return <StatusBadge status="completed" size="sm" label={m.admin_invites_status_accepted()} />;
  }
  if (status === 'replaced') {
    return <StatusBadge status="terminated" size="sm" label={m.admin_invites_status_replaced()} />;
  }
  return <StatusBadge status="needs_review" size="sm" label={m.admin_invites_status_expired()} />;
}
